<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <!-- === Authorization Check Restored === -->
      <!-- 1. Show main content IF admin_datos loaded AND user is superadmin -->
      <div v-if="admin_datos && admin_datos.superadmin" class="main-container">

        <!-- Welcome Message -->
        <div style="margin-bottom: 20px; padding: 15px; background-color: #eef7ff; border-radius: 8px; border: 1px solid #cce5ff;">
           <h2 style="margin-top:0;">¡Muy buenos días {{admin_datos.nombre}}...! <img src="@/assets/logo-jobbing-nuevo.webp" style="height:40px; vertical-align: middle;" alt="Logo"></h2>
          El propósito de este panel es ayudarte en tu negocio 🚀.
        </div>

        <!-- Global Loading State (Only for Initial Load) -->
        <div v-if="isInitialLoading" class="articulo-principal loading-indicator">
          <p>Cargando datos iniciales del panel...</p>
        </div>

        <!-- Global Error State -->
        <div v-else-if="errorLoading && !Object.values(sectionIsLoading).some(l => l)" class="articulo-principal error-message"> <!-- Show global error only if no section is specifically loading -->
           <p>Error al cargar los datos del panel: {{ errorLoading }}</p>
           <button @click="fetchDashboardData">Intentar de nuevo</button>
        </div>

        <!-- Data Grid (Shown after initial load, handles per-section loading) -->
        <div v-else class="articulo-principal">
          <div class="dashboard-layout-actions">
            <div class="dashboard-refresh-status">
              <button
                type="button"
                class="btn btn-std"
                :disabled="dashboardRefreshButtonDisabled"
                @click="refreshDashboardManually"
              >
                {{ dashboardIsRefreshing ? 'Actualizando...' : 'Actualizar' }}
              </button>
              <span class="dashboard-updated-at">{{ dashboardUpdatedAgoText }}</span>
            </div>
            <button type="button" class="btn btn-std" @click="toggleFullWidthTables">
              {{ useFullWidthTables ? 'Usar dos tablas por fila' : 'Usar tablas en ancho completo' }}
            </button>
          </div>
          <div class="dashboard-grid" :class="{ 'single-column': useFullWidthTables }">

            <!-- Solicitudes compradas -->
            <div class="dashboard-section">
              <div class="section-header">
                <h3>Solicitudes compradas</h3>
                <div class="section-pagination-controls">
                  <button @click="changeSectionPage('solicitudesCompradas', -1)" :disabled="sectionPages.solicitudesCompradas <= 1 || sectionIsLoading.solicitudesCompradas || isInitialLoading">«</button>
                  <span>Pág. {{ sectionPages.solicitudesCompradas }} de {{ comprasTotalPages }}</span>
                  <button @click="changeSectionPage('solicitudesCompradas', 1)" :disabled="sectionIsLastPage.solicitudesCompradas || sectionIsLoading.solicitudesCompradas || isInitialLoading">»</button>
                </div>
              </div>
              <form class="compras-filters" @submit.prevent="applyComprasFilters">
                <input v-model.trim="comprasSearch" type="search" aria-label="Buscar solicitudes compradas" placeholder="ID, profesional, cliente, título, categoría o comuna" />
                <label>Desde <input v-model="comprasDesde" type="date" /></label>
                <label>Hasta <input v-model="comprasHasta" type="date" /></label>
                <button class="btn btn-std" type="submit" :disabled="sectionIsLoading.solicitudesCompradas">Filtrar</button>
                <button class="btn btn-std" type="button" :disabled="sectionIsLoading.solicitudesCompradas" @click="clearComprasFilters">Limpiar</button>
              </form>
              <p class="meta-muted">{{ comprasTotal.toLocaleString('es-CL') }} compras · {{ comprasTotalPuntos.toLocaleString('es-CL') }} puntos pagados. Cada fila corresponde a la compra de un profesional.</p>
              <div class="table-container">
                <div v-if="sectionIsLoading.solicitudesCompradas" class="section-loader">Cargando compras...</div>
                <div v-else-if="comprasError" class="error-message">
                  {{ comprasError }}
                  <button type="button" @click="fetchSolicitudesCompradas()">Reintentar</button>
                </div>
                <table v-else>
                  <thead><tr><th>Solicitud</th><th>Profesional</th><th>Cliente</th><th>Fecha de compra</th><th>Precio (puntos)</th><th>Categoría</th><th>Comuna</th></tr></thead>
                  <tbody>
                    <tr v-for="compra in solicitudesCompradas" :key="compra.solicitud_id + '-' + compra.profesional_id">
                      <td><button type="button" class="dashboard-entity-link" @click="navigateToSolicitud(compra.solicitud_id)">#{{ compra.solicitud_id }}</button><div>{{ compra.titulo }}</div></td>
                      <td><button type="button" class="dashboard-entity-link" @click="navigateToProfessional(compra.profesional_id)">{{ compra.profesional_nombre }}</button></td>
                      <td><button v-if="compra.cliente_id" type="button" class="dashboard-entity-link" @click="navigateToClient(compra.cliente_id)">{{ compra.cliente_nombre }}</button><span v-else>{{ compra.cliente_nombre }}</span></td>
                      <td>{{ formatDisplayDate(compra.fecha_compra) }}</td>
                      <td>{{ compra.puntos_jobbing_gastados.toLocaleString('es-CL') }} pts</td>
                      <td>{{ compra.categoria || 'Sin categoría' }}</td><td>{{ compra.comuna || 'Sin comuna' }}</td>
                    </tr>
                    <tr v-if="!solicitudesCompradas.length"><td colspan="7">No hay compras para los filtros seleccionados.</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <!-- End Solicitudes compradas -->

             <!-- Próximas Visitas -->
             <div class="dashboard-section">
               <div class="section-header">
                  <h3>Próximas Visitas</h3>
                  <div class="section-pagination-controls">
                    <button @click="changeSectionPage('proximasVisitas', -1)" :disabled="sectionPages.proximasVisitas <= 1 || sectionIsLoading.proximasVisitas || isInitialLoading">«</button>
                    <span>Pág. {{ sectionPages.proximasVisitas }}</span>
                    <button @click="changeSectionPage('proximasVisitas', 1)" :disabled="sectionIsLastPage.proximasVisitas || sectionIsLoading.proximasVisitas || isInitialLoading">»</button>
                  </div>
               </div>
               <div class="table-container">
                  <div v-if="sectionIsLoading.proximasVisitas" class="section-loader">Cargando...</div>
                  <table v-else>
                     <thead><tr><th>Fecha y Hora</th><th>Dirección</th><th>Comuna</th><th>Profesional</th><th>Cliente</th><th>Costo Estimado</th><th>Estado</th><th>Acciones</th></tr></thead>
                     <tbody>
                        <tr v-if="proximasVisitas.length" v-for="visita in proximasVisitas" :key="'vis-'+visita.id">
                           <td>
                             <div v-if="editingVisitaId === visita.id" class="visita-edit-datetime">
                               <input v-model="visitaEditDraft.fecha" type="date" class="visita-edit-input" />
                               <input v-model="visitaEditDraft.hora" type="time" step="60" class="visita-edit-input" />
                             </div>
                             <span v-else>{{ formatDisplayDate(visita.fecha, false) }} {{ visita.hora }}</span>
                           </td>
                           <td>{{ visita.direccion }}</td><td>{{ visita.comuna }}</td>
                            <td>{{ visita.profesional_nombre }}</td>
                            <td>
                              <button
                                v-if="visita.cliente_id"
                                type="button"
                                class="dashboard-entity-link"
                                @click.stop="navigateToClient(visita.cliente_id)"
                              >
                                {{ visita.cliente_nombre }}
                              </button>
                              <span v-else>{{ visita.cliente_nombre }}</span>
                            </td>
                           <td>{{ formatCurrency(visita.costo_estimado_proyecto) }}</td>
                           <td><span :class="['badge', mapVisitaStatus(visita.estado).className]">{{ mapVisitaStatus(visita.estado).text }}</span></td>
                           <td>
                             <div class="visita-actions">
                               <template v-if="editingVisitaId === visita.id">
                                 <button
                                   class="icon-action-button"
                                   title="Guardar cambios"
                                   :disabled="isVisitaUpdating(visita.id) || isVisitaRemoving(visita.id)"
                                   @click="saveVisitaEdit(visita)"
                                 >
                                   <span v-if="!isVisitaUpdating(visita.id)">💾</span>
                                   <span v-else>…</span>
                                 </button>
                                 <button
                                   class="icon-action-button"
                                   title="Cancelar edición"
                                   :disabled="isVisitaUpdating(visita.id)"
                                   @click="cancelVisitaEdit"
                                 >
                                   ✕
                                 </button>
                               </template>
                               <template v-else>
                                 <button
                                   class="icon-action-button"
                                   title="Editar fecha/hora"
                                   :disabled="isVisitaUpdating(visita.id) || isVisitaRemoving(visita.id)"
                                   @click="startVisitaEdit(visita)"
                                 >
                                   ✏️
                                 </button>
                                 <button
                                   class="icon-action-button"
                                   title="Eliminar visita"
                                   :disabled="isVisitaUpdating(visita.id) || isVisitaRemoving(visita.id)"
                                   @click="removeVisitaWithConfirmation(visita)"
                                 >
                                   <span v-if="!isVisitaRemoving(visita.id)">🗑️</span>
                                   <span v-else>…</span>
                                 </button>
                               </template>
                             </div>
                           </td>
                        </tr>
                        <tr v-else><td colspan="8">No hay visitas programadas en esta página.</td></tr>
                     </tbody>
                  </table>
               </div>
             </div>
             <!-- End Próximas Visitas -->

             <!-- Presupuestos pendientes de enviar -->
             <div class="dashboard-section">
               <div class="section-header">
                  <h3>Presupuestos pendientes</h3>
                  <div class="section-actions">
                    <div class="section-filter">
                      <input
                        type="text"
                        v-model="profesionalPendienteQuery"
                        placeholder="Buscar profesional..."
                        class="input-buscar-profesional"
                      />
                      <div
                        v-if="profesionalPendienteQuery && resultadoProfesionalesPendientes.length"
                        class="resultado-profesional"
                      >
                        <div
                          v-for="r in resultadoProfesionalesPendientes.slice(0,10)"
                          :key="'pend-'+r.item.id"
                          @click="selectProfesionalPendiente(r.item)"
                          class="resultado-profesional-item"
                        >
                          {{ r.item.nombre }}
                        </div>
                      </div>
                      <div v-if="profesionalPendienteSeleccionado" class="profesional-seleccionado">
                        Filtrado por: {{ profesionalPendienteSeleccionado.nombre }}
                        <button class="clear-filter" @click="clearProfesionalPendienteFilter">×</button>
                      </div>
                    </div>
                    <div class="section-pagination-controls">
                      <button @click="changeSectionPage('presupuestosPendientesEnviar', -1)" :disabled="sectionPages.presupuestosPendientesEnviar <= 1 || sectionIsLoading.presupuestosPendientesEnviar || isInitialLoading">«</button>
                      <span>Pág. {{ sectionPages.presupuestosPendientesEnviar }}</span>
                      <button @click="changeSectionPage('presupuestosPendientesEnviar', 1)" :disabled="sectionIsLastPage.presupuestosPendientesEnviar || sectionIsLoading.presupuestosPendientesEnviar || isInitialLoading">»</button>
                    </div>
                  </div>
               </div>
               <div class="table-container">
                 <div v-if="sectionIsLoading.presupuestosPendientesEnviar" class="section-loader">Cargando...</div>
                 <table v-else>
                   <thead><tr><th></th><th>ID Orden</th><th>ID Solicitud</th><th>Profesional</th><th>Cliente</th><th>Dirección</th><th>Días Pendiente</th><th>Acciones</th></tr></thead>
                   <tbody>
                      <tr v-if="presupuestosPendientesEnviar.length" v-for="presupuesto in presupuestosPendientesEnviar" :key="'pend-'+presupuesto.orden_id">
                        <td><span v-if="presupuesto.diasPendiente >= 5" class="warning-icon" title="Plazo vencido">⚠️</span></td>
                        <td>
                          <button
                            v-if="presupuesto.orden_id"
                            type="button"
                            class="dashboard-entity-link"
                            @click.stop="navigateToOrder(presupuesto.orden_id)"
                          >
                            #{{ presupuesto.orden_id }}
                          </button>
                          <span v-else>-</span>
                        </td>
                        <td>{{ presupuesto.solicitud_id }}</td>
                        <td>{{ presupuesto.profesional_nombre }}</td>
                        <td>
                          <button
                            v-if="presupuesto.cliente_id"
                            type="button"
                            class="dashboard-entity-link"
                            @click.stop="navigateToClient(presupuesto.cliente_id)"
                          >
                            {{ presupuesto.cliente }}
                          </button>
                          <span v-else>{{ presupuesto.cliente }}</span>
                        </td>
                        <td>{{ presupuesto.direccion }}</td><td>{{ presupuesto.diasPendiente }}</td>
                       <td>
                         <button
                           class="icon-action-button"
                           :disabled="isPresupuestoRemovalInProgress(presupuesto.orden_id)"
                           @click="markPresupuestoNoRequerido(presupuesto.orden_id)"
                           title="Marcar presupuesto no requerido"
                         >
                           <span v-if="!isPresupuestoRemovalInProgress(presupuesto.orden_id)">🗑️</span>
                           <span v-else>…</span>
                         </button>
                       </td>
                     </tr>
                     <tr v-else><td colspan="8">No hay presupuestos pendientes en esta página.</td></tr>
                   </tbody>
                 </table>
             </div>
            </div>
            <!-- End Presupuestos pendientes -->

            <!-- Jobbing B2B -->
            <div class="dashboard-section">
              <div class="section-header jobbing-b2b-header">
                <h3>Jobbing B2B</h3>
                <div class="section-actions jobbing-b2b-actions">
                  <div class="jobbing-b2b-filter-row">
                    <div class="section-filter">
                      <label for="jobbing-b2b-empresa">Empresa:</label>
                      <select
                        id="jobbing-b2b-empresa"
                        v-model="jobbingB2BEmpresaSelection"
                        class="select-filtro"
                        @change="addJobbingEmpresaFilter"
                      >
                        <option value="">Todas</option>
                        <option
                          v-for="empresa in empresasAdministracion"
                          :key="'empresa-'+empresa.id"
                          :value="empresa.id"
                        >
                          {{ empresa.nombre }}
                        </option>
                      </select>
                    </div>
                    <div class="section-filter">
                      <label for="jobbing-b2b-from">Desde:</label>
                      <input
                        id="jobbing-b2b-from"
                        type="date"
                        v-model="jobbingB2BFromDate"
                        @change="applyJobbingFromDateFilter"
                      />
                    </div>
                    <div class="section-filter">
                      <label for="jobbing-b2b-alerts">Alertas:</label>
                      <select
                        id="jobbing-b2b-alerts"
                        v-model="jobbingB2BWarningFilter"
                        class="select-filtro"
                      >
                        <option value="all">Todas</option>
                        <option value="with">Con alerta</option>
                        <option value="without">Sin alerta</option>
                      </select>
                    </div>
                    <div class="section-filter">
                      <label for="jobbing-b2b-estado">Estado:</label>
                      <select
                        id="jobbing-b2b-estado"
                        v-model="jobbingB2BEstadoSelection"
                        class="select-filtro"
                        @change="addJobbingEstadoFilter"
                      >
                        <option value="">Todos</option>
                        <option
                          v-for="estado in jobbingB2BEstadoOptions"
                          :key="'estado-'+estado.value"
                          :value="estado.value"
                        >
                          {{ estado.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div
                    v-if="jobbingB2BEmpresaFilters.length || jobbingB2BEstadoFilters.length"
                    class="jobbing-b2b-selected-chips"
                  >
                    <span
                      v-for="empresaId in jobbingB2BEmpresaFilters"
                      :key="'empresa-chip-'+empresaId"
                      class="filter-chip"
                    >
                      {{ getEmpresaNombre(empresaId) }}
                      <button class="chip-remove" @click="removeJobbingEmpresaFilter(empresaId)" aria-label="Quitar empresa">
                        ×
                      </button>
                    </span>
                    <span
                      v-for="estadoValue in jobbingB2BEstadoFilters"
                      :key="'estado-chip-'+estadoValue"
                      class="filter-chip"
                    >
                      {{ getEstadoLabel(estadoValue) }}
                      <button class="chip-remove" @click="removeJobbingEstadoFilter(estadoValue)" aria-label="Quitar estado">
                        ×
                      </button>
                    </span>
                  </div>
                </div>
                </div>
                <div class="table-container">
                  <div v-if="sectionIsLoading.jobbingB2B" class="section-loader">Cargando...</div>
                  <table v-else>
                  <thead>
                    <tr>
                      <th></th>
                      <th class="sortable" @click="changeJobbingB2BSort('id')">ID</th>
                      <th>Dirección</th>
                      <th class="sortable" @click="changeJobbingB2BSort('empresa')">Empresa</th>
                      <th class="sortable" @click="changeJobbingB2BSort('fecha_creado')">Creada</th>
                      <th class="sortable" @click="changeJobbingB2BSort('profesional')">Profesional</th>
                      <th class="sortable" @click="changeJobbingB2BSort('estado')">Estado</th>
                      <th class="sortable" @click="changeJobbingB2BSort('presupuesto_fecha')">Presupuesto</th>
                      <th class="sortable" @click="changeJobbingB2BSort('factura')">Factura</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="sortedJobbingB2BRequests.length">
                      <template v-for="sol in sortedJobbingB2BRequests">
                        <tr
                          :key="'b2b-'+sol.id"
                          class="clickable-row"
                          @click="toggleJobbingB2BRow(sol.id)"
                        >
                          <td>
                            <div class="warning-icon-stack">
                              <span
                                v-if="sol.alerta_sin_presupuesto"
                                class="warning-icon"
                                title="Solicitud sin presupuesto por más de 5 días"
                              >⚠️</span>
                              <span
                                v-if="sol.alerta_realizado_sin_factura"
                                class="warning-icon"
                                title="Hay ítems realizados sin factura cargada"
                              >⚠️</span>
                            <span
                              v-if="sol.alerta_aprobado_no_realizado"
                              class="warning-icon"
                              title="Hay ítems aprobados sin realizar por más de 5 días"
                            >⚠️</span>
                          </div>
                        </td>
                        <td>#{{ sol.id }}</td>
                        <td>
                          <div>{{ sol.solicitud_direccion || 'Sin dirección' }}</div>
                          <div class="meta-muted">{{ getComunaNombre(sol.solicitud_comuna_id) }}</div>
                        </td>
                        <td>{{ sol.empresa_nombre || 'Sin empresa' }}</td>
                        <td>
                          <div>{{ formatDisplayDate(sol.fecha_creado, false) }}</div>
                          <div v-if="sol.dias_desde_creado !== null" class="meta-muted">({{ formatDaysAgo(sol.dias_desde_creado) }})</div>
                        </td>
                          <td>
                            <div>{{ sol.orden && sol.orden.profesional_nombre ? sol.orden.profesional_nombre : '—' }}</div>
                            <div v-if="sol.orden" class="meta-muted">{{ formatDisplayDate(sol.orden.fecha_creado, false) }}</div>
                            <div
                              v-if="sol.orden && sol.orden.dias_desde_creado !== null"
                              class="meta-muted"
                            >
                              ({{ formatDaysAgo(sol.orden.dias_desde_creado) }})
                            </div>
                          </td>
                          <td>
                            <span
                              class="badge"
                              :style="{
                                backgroundColor: sol.estado && sol.estado.color ? sol.estado.color : '#e0e0e0',
                                color: '#000',
                              }"
                            >
                              {{ (sol.estado && sol.estado.label) || 'N/A' }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="badge presupuesto-badge"
                              :class="getPresupuestoCount(sol) > 0 ? 'presupuesto-present' : 'presupuesto-absent'"
                              :title="presupuestoTooltip(sol)"
                            >
                              {{ getPresupuestoCount(sol) > 0 ? 'Con presupuesto' : 'Sin presupuesto' }}
                            </span>
                            <template v-if="sol.presupuesto && getPresupuestoCount(sol) > 0">
                              <div class="meta-muted">{{ formatDisplayDate(sol.presupuesto.fecha_creado, false) }}</div>
                              <div v-if="sol.presupuesto.dias_desde_creado !== null" class="meta-muted">({{ formatDaysAgo(sol.presupuesto.dias_desde_creado) }})</div>
                            </template>
                          </td>
                          <td>
                            <span
                              class="badge factura-badge"
                              :class="sol.orden && sol.orden.tiene_factura ? 'factura-present' : 'factura-absent'"
                              :title="facturaTooltip(sol)"
                            >
                              {{ sol.orden && sol.orden.tiene_factura ? 'Factura' : 'Sin factura' }}
                            </span>
                          </td>
                        </tr>
                        <tr
                          :key="'b2b-detail-'+sol.id"
                          v-show="isJobbingB2BExpanded(sol.id)"
                          class="jobbing-b2b-detail"
                          @click.stop
                        >
                          <td colspan="9">
                            <div class="jobbing-b2b-detail-header">
                              <div class="detail-title"><strong>Título:</strong> {{ sol.titulo || 'Sin título' }}</div>
                              <div class="detail-description">
                                <strong>Descripción:</strong>
                                <span>{{ sol.descripcion || 'Sin descripción' }}</span>
                              </div>
                            </div>
                            <div class="jobbing-items">
                              <div class="jobbing-items-title">Items cargados</div>
                              <template v-if="sol.orden && sol.orden.items && sol.orden.items.length">
                                <table class="jobbing-items-table">
                                  <thead>
                                    <tr>
                                      <th>Nombre</th>
                                      <th>Descripción</th>
                                      <th>Estado</th>
                                      <th>Fecha realizado</th>
                                      <th>Aprobado</th>
                                      <th>Precio</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="item in sol.orden.items" :key="'item-'+item.id">
                                      <td>{{ item.nombre || 'Sin nombre' }}</td>
                                      <td>{{ item.descripcion || '—' }}</td>
                                      <td>
                                        <span :class="['badge', item.realizado ? 'badge-success' : 'badge-muted']">
                                          {{ item.realizado ? 'Realizado' : 'Pendiente' }}
                                        </span>
                                      </td>
                                      <td>{{ item.fecha_realizado ? formatDisplayDate(item.fecha_realizado, false) : '—' }}</td>
                                      <td>
                                        <span
                                          v-if="item.aprobado_presupuesto"
                                          class="badge badge-warning"
                                          title="Ítem aprobado en el presupuesto"
                                        >
                                          Aprobado
                                        </span>
                                        <span v-else class="meta-muted">—</span>
                                        <div
                                          v-if="item.aprobado_overdue"
                                          class="badge badge-danger overdue-badge"
                                          :title="overdueItemTooltip(item)"
                                        >
                                          Pendiente (>5 días)
                                        </div>
                                      </td>
                                      <td>{{ item.precio != null ? formatCurrency(item.precio) : '—' }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </template>
                              <div v-else class="meta-muted">Sin ítems cargados.</div>
                            </div>
                            <div class="jobbing-pagos">
                              <div class="jobbing-items-title">Pagos</div>
                              <template v-if="sol.orden && sol.orden.pagos && sol.orden.pagos.length">
                                <table class="jobbing-items-table jobbing-pagos-table">
                                  <thead>
                                    <tr>
                                      <th>ID</th>
                                      <th>Monto</th>
                                      <th>Estado</th>
                                      <th>Fecha creado</th>
                                      <th>Fecha pagado</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="pago in sol.orden.pagos" :key="'pago-'+pago.id">
                                      <td>#{{ pago.id }}</td>
                                      <td>{{ pago.monto != null ? formatCurrency(pago.monto) : '—' }}</td>
                                      <td>
                                        <span class="badge pago-badge" :class="getPagoEstadoClass(pago.estado)">
                                          {{ pago.estado_label || 'Desconocido' }}
                                        </span>
                                      </td>
                                      <td>{{ pago.fecha_creado ? formatDisplayDate(pago.fecha_creado, false) : '—' }}</td>
                                      <td>{{ pago.fecha_pagado ? formatDisplayDate(pago.fecha_pagado, false) : '—' }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </template>
                              <div v-else class="meta-muted">Sin pagos registrados.</div>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </template>
                    <tr v-else><td colspan="9">No hay solicitudes B2B para el rango seleccionado.</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <!-- End Jobbing B2B -->

            <!-- Warm Conversations -->
            <div class="dashboard-section">
              <div class="section-header">
                <h3>Posibles conversiones</h3>
                <div class="section-actions warm-filters">
                  <div class="warm-filter-group">
                    <label class="filter-label">Profesional:</label>
                    <input
                      type="text"
                      v-model="warmProfessionalQuery"
                      placeholder="Buscar profesional..."
                      class="input-buscar-profesional"
                    />
                    <div
                      v-if="warmProfessionalQuery && warmProfessionalResults.length"
                      class="resultado-profesional"
                    >
                      <div
                        v-for="r in warmProfessionalResults.slice(0,10)"
                        :key="'warm-'+r.item.id"
                        @click="addWarmProfessional(r.item)"
                        class="resultado-profesional-item"
                      >
                        {{ r.item.nombre }}
                      </div>
                    </div>
                    <div v-if="warmProfessionalFilters.length" class="selected-chips">
                      <span
                        class="filter-chip"
                        v-for="prof in warmProfessionalFilters"
                        :key="'warm-chip-'+prof.id"
                      >
                        {{ prof.nombre }}
                        <button class="chip-remove" @click="removeWarmProfessional(prof.id)" aria-label="Quitar profesional">×</button>
                      </span>
                    </div>
                  </div>

                  <div class="warm-filter-group">
                    <label class="filter-label" for="warm-start-date">Desde:</label>
                    <input
                      id="warm-start-date"
                      type="date"
                      v-model="warmStartDate"
                      class="input-buscar-profesional"
                    />
                  </div>

                  <div class="warm-filter-group">
                    <label class="filter-label">Interés:</label>
                    <div class="interest-chips">
                      <button
                        v-for="option in warmInterestOptions"
                        :key="option.value"
                        type="button"
                        class="chip-button"
                        :class="{ active: warmInterestFilters.includes(option.value) }"
                        @click="toggleWarmInterest(option.value)"
                      >
                        {{ option.label }}
                      </button>
                    </div>
                  </div>

                  <div class="warm-filter-group last-message-toggle">
                    <label class="filter-label">
                      <input type="checkbox" v-model="onlyClientLastMessage" />
                      Último mensaje del cliente
                    </label>
                  </div>

                  <div class="warm-filter-group warm-copy-group">
                    <label class="filter-label">Clientes:</label>
                    <button
                      type="button"
                      class="btn btn-std warm-copy-button"
                      :disabled="!filteredWarmConversations.length || warmProfessionalFilters.length !== 1"
                      @click="copyWarmClientList"
                    >
                      Copiar lista
                    </button>
                  </div>

                  <div class="section-pagination-controls">
                    <button @click="changeSectionPage('warmConversations', -1)" :disabled="sectionPages.warmConversations <= 1 || sectionIsLoading.warmConversations || isInitialLoading">«</button>
                    <span>Pág. {{ sectionPages.warmConversations }}</span>
                    <button @click="changeSectionPage('warmConversations', 1)" :disabled="warmIsLastPage || sectionIsLoading.warmConversations || isInitialLoading">»</button>
                  </div>
                </div>
              </div>
              <div class="table-container">
                <div v-if="sectionIsLoading.warmConversations" class="section-loader">Cargando...</div>
                <table v-else>
                  <thead>
                    <tr>
                      <th class="sortable" @click="changeWarmSort('solicitud_cliente_id')">ID Solicitud</th>
                      <th class="sortable" @click="changeWarmSort('solicitud_fecha')">Fecha Solicitud</th>
                      <th class="sortable" @click="changeWarmSort('cliente_nombre')">Cliente</th>
                      <th class="sortable" @click="changeWarmSort('profesional_nombre')">Profesional</th>
                      <th class="sortable" @click="changeWarmSort('solicitud_clase')">Clase</th>
                      <th class="sortable" @click="changeWarmSort('interest')">Interés</th>
                      <th class="sortable" @click="changeWarmSort('client_requests_extra_info')">Info extra</th>
                      <th class="sortable" @click="changeWarmSort('updated_at')">Última actualización</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="paginatedWarmConversations.length">
                      <template v-for="conv in paginatedWarmConversations">
                        <tr
                          class="clickable-row"
                          :key="'warm-'+conv.solicitud_cliente_id+'-'+conv.profesional_id"
                          @click="toggleWarmConversation(conv)"
                        >
                          <td>{{ conv.solicitud_cliente_id }}</td>
                          <td>{{ formatDisplayDate(conv.solicitud_fecha) }}</td>
                          <td>
                            <button
                              v-if="conv.cliente_id"
                              type="button"
                              class="dashboard-entity-link"
                              @click.stop="navigateToClient(conv.cliente_id)"
                            >
                              {{ conv.cliente_nombre }}
                            </button>
                            <span v-else>{{ conv.cliente_nombre }}</span>
                          </td>
                          <td>{{ conv.profesional_nombre }}</td>
                          <td>
                            <span
                              class="badge"
                              :class="mapSolicitudClase(conv.solicitud_clase).className"
                            >
                              {{ mapSolicitudClase(conv.solicitud_clase).text }}
                            </span>
                          </td>
                          <td>
                            <span
                              v-if="conv.response_json && conv.response_json.interest_score != null"
                              class="badge"
                              :class="mapInterestScore(conv.response_json.interest_score).className"
                              :title="conv.response_json.interest_score"
                            >
                              {{ mapInterestScore(conv.response_json.interest_score).text }}
                            </span>
                            <span v-else>-</span>
                          </td>
                          <td>
                            <span
                              :class="['badge', conv.client_requests_extra_info ? 'status-warning' : 'status-neutral']"
                            >
                              {{ conv.client_requests_extra_info ? 'Sí' : 'No' }}
                            </span>
                          </td>
                          <td>{{ formatDisplayDate(conv.updated_at) }}</td>
                          <td>
                            <button
                              class="btn btn-std"
                              v-if="!conv.resolved"
                              @click.stop="markWarmConversationResolved(conv.scan_id)"
                            >
                              Resolver
                            </button>
                          </td>
                        </tr>
                        <tr
                          v-if="expandedWarmConversationId === conv.scan_id"
                          class="warm-conversation-detail-row"
                          :key="'warm-detail-'+conv.scan_id"
                        >
                          <td colspan="9">
                            <div class="warm-conversation-panel">
                              <div class="panel-header">
                                <div>
                                  <strong>Cliente:</strong> {{ conv.cliente_nombre }}<br />
                                  <strong>Profesional:</strong> {{ conv.profesional_nombre }}
                                </div>
                                <div class="panel-meta">
                                  <div>Solicitud #{{ conv.solicitud_cliente_id }}</div>
                                  <div>
                                    Info extra: {{ conv.client_requests_extra_info ? 'Sí' : 'No' }} •
                                    Último mensaje: {{ isLastMessageFromClient(conv) ? 'Cliente' : 'Profesional' }}
                                  </div>
                                </div>
                              </div>
                              <div class="panel-details">
                                <div>
                                  <strong>Título:</strong> {{ conv.solicitud_titulo || 'Sin título' }}
                                </div>
                                <div>
                                  <strong>Descripción:</strong> {{ conv.solicitud_descripcion || 'Sin descripción' }}
                                </div>
                                <div>
                                  <strong>Clase:</strong> {{ mapSolicitudClase(conv.solicitud_clase).text }}
                                </div>
                                <div>
                                  <strong>Comuna:</strong> {{ getComunaNombre(conv.solicitud_comuna_id) }}
                                </div>
                              </div>
                              <div v-if="warmConversationLoading" class="panel-body">Cargando conversación...</div>
                              <div v-else-if="warmConversationError" class="panel-body error">{{ warmConversationError }}</div>
                              <div v-else class="panel-body messages">
                                <div
                                  v-if="currentWarmMessages.length"
                                  v-for="message in currentWarmMessages"
                                  :key="'msg-'+message.sender+'-'+message.id"
                                  class="message-row"
                                >
                                  <div class="message-meta">
                                    <span class="sender" :class="message.sender.toLowerCase()">{{ message.sender }}</span>
                                    <span class="timestamp">{{ formatDisplayDate(message.fecha) }}</span>
                                  </div>
                                  <div class="message-text">{{ message.mensaje || '—' }}</div>
                                </div>
                                <div v-else class="empty-messages">No hay mensajes disponibles.</div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </template>
                      <tr v-else><td colspan="9">No hay conversaciones.</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <!-- End Warm Conversations -->

            <!-- Solicitudes sin Respuesta -->
            <div class="dashboard-section">
              <div class="section-header">
                <h3>Solicitudes sin Respuesta</h3>
                <div class="section-actions">
                  <div class="section-filter">
                    <label for="solicitudesTipoFiltro">Tipo:</label>
                    <select id="solicitudesTipoFiltro" v-model="solicitudesTipoFiltro" class="select-filtro">
                      <option value="Todas">Ambas</option>
                      <option value="Publica">Pública</option>
                      <option value="Privada">Privada</option>
                    </select>
                  </div>
                  <div class="section-pagination-controls">
                    <button @click="changeSectionPage('solicitudesSinRespuesta', -1)" :disabled="sectionPages.solicitudesSinRespuesta <= 1 || sectionIsLoading.solicitudesSinRespuesta || isInitialLoading">«</button>
                    <span>Pág. {{ sectionPages.solicitudesSinRespuesta }}</span>
                    <button @click="changeSectionPage('solicitudesSinRespuesta', 1)" :disabled="sectionIsLastPage.solicitudesSinRespuesta || sectionIsLoading.solicitudesSinRespuesta || isInitialLoading">»</button>
                  </div>
                </div>
              </div>
               <div class="table-container">
                 <div v-if="sectionIsLoading.solicitudesSinRespuesta" class="section-loader">Cargando...</div>
                 <table v-else>
                   <thead><tr><th>Cliente</th><th>Título</th><th>Descripción (inicio)</th><th>Fecha</th><th>Categoría</th><th>Tipo</th></tr></thead>
                   <tbody>
                    <tr v-if="filteredSolicitudesSinRespuesta.length" v-for="solicitud in filteredSolicitudesSinRespuesta" :key="'sol-'+solicitud.id">
                      <td>
                        <button
                          v-if="solicitud.cliente_id"
                          type="button"
                          class="dashboard-entity-link"
                          @click.stop="navigateToClient(solicitud.cliente_id)"
                        >
                          {{ solicitud.cliente_nombre }}
                        </button>
                        <span v-else>{{ solicitud.cliente_nombre }}</span>
                      </td>
                      <td>{{ solicitud.titulo }}</td><td>{{ solicitud.descripcion_corta }}...</td><td>{{ formatDisplayDate(solicitud.fecha) }}</td>
                      <td>{{ solicitud.categoria }}</td>
                      <td><span :class="['badge', mapSolicitudType(solicitud.tipo).className]">{{ mapSolicitudType(solicitud.tipo).text }}</span>
                        <span v-if="solicitud.tipo === 'Privada' && solicitud.profesional_asignado" class="profesional-detail">({{ solicitud.profesional_asignado }})</span></td>
                    </tr>
                    <tr v-else><td colspan="6">No hay solicitudes sin respuesta en esta página.</td></tr>
                   </tbody>
                 </table>
               </div>
            </div>
            <!-- End Solicitudes -->

            <ReviewReports />

            <div id="reportes-contacto" class="dashboard-section contact-reports-section">
              <div class="section-header contact-reports-header">
                <div>
                  <h3>Reportes de Contacto</h3>
                  <p class="section-subtitle">Solicitudes compradas con puntos donde el profesional reportó datos incorrectos.</p>
                </div>
                <div class="section-actions contact-reports-actions">
                  <div class="section-filter">
                    <label for="contact-reports-status">Estado:</label>
                    <select
                      id="contact-reports-status"
                      v-model="contactReportsStatusFilter"
                      class="select-filtro"
                      @change="handleContactReportsStatusChange"
                    >
                      <option value="pendiente">Pendientes</option>
                      <option value="all">Todos</option>
                      <option value="aprobado">Aprobados</option>
                      <option value="rechazado">Rechazados</option>
                      <option value="resuelto">Resueltos</option>
                    </select>
                  </div>
                  <div class="section-pagination-controls">
                    <button @click="changeSectionPage('contactReports', -1)" :disabled="sectionPages.contactReports <= 1 || sectionIsLoading.contactReports || isInitialLoading">«</button>
                    <span>Pág. {{ sectionPages.contactReports }}</span>
                    <button @click="changeSectionPage('contactReports', 1)" :disabled="sectionIsLastPage.contactReports || sectionIsLoading.contactReports || isInitialLoading">»</button>
                  </div>
                </div>
              </div>
              <div class="table-container">
                <div v-if="sectionIsLoading.contactReports" class="section-loader">Cargando...</div>
                <table v-else>
                  <thead>
                    <tr>
                      <th>Solicitud</th>
                      <th>Profesional</th>
                      <th>Motivo</th>
                      <th>Detalle</th>
                      <th>Contacto mostrado</th>
                      <th>Puntos</th>
                      <th>Fecha</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-if="contactReports.length"
                      v-for="report in contactReports"
                      :key="'contact-report-'+report.id"
                      :class="['contact-report-row', contactReportRowClass(report)]"
                    >
                      <td>
                        <button
                          v-if="report.solicitud && report.solicitud.id"
                          type="button"
                          class="dashboard-entity-link"
                          @click.stop="navigateToSolicitud(report.solicitud.id)"
                        >
                          #{{ report.solicitud.id }} - {{ report.solicitud.titulo || 'Solicitud' }}
                        </button>
                        <span v-else>#{{ report.solicitud_cliente_id }}</span>
                        <div v-if="report.solicitud && (report.solicitud.direccion || report.solicitud.comuna)" class="meta-muted">
                          {{ [report.solicitud.direccion, report.solicitud.comuna].filter(Boolean).join(', ') }}
                        </div>
                      </td>
                      <td>
                        <button
                          v-if="report.profesional && report.profesional.id"
                          type="button"
                          class="dashboard-entity-link"
                          @click.stop="navigateToProfessional(report.profesional.id)"
                        >
                          {{ report.profesional.nombre || ('Profesional #' + report.profesional.id) }}
                        </button>
                        <span v-else>#{{ report.profesional_id }}</span>
                      </td>
                      <td><span class="badge contact-report-reason">{{ report.motivo_label || report.motivo || '-' }}</span></td>
                      <td class="contact-report-detail">{{ report.detalle || '-' }}</td>
                      <td>
                        <div class="contact-report-contact">
                          <div>{{ getContactReportSnapshotValue(report, 'nombre') || '-' }}</div>
                          <div v-if="getContactReportSnapshotValue(report, 'telefono')" class="meta-muted">{{ getContactReportSnapshotValue(report, 'telefono') }}</div>
                          <div v-if="getContactReportSnapshotValue(report, 'email')" class="meta-muted">{{ getContactReportSnapshotValue(report, 'email') }}</div>
                        </div>
                      </td>
                      <td>{{ Number(report.puntos_jobbing_gastados || 0).toLocaleString('es-CL') }} pts</td>
                      <td>{{ formatDisplayDate(report.created_at) }}</td>
                      <td><span :class="['badge', contactReportStatusClass(report)]">{{ contactReportStatusLabel(report) }}</span></td>
                      <td>
                        <div class="refund-action-buttons">
                          <button
                            type="button"
                            class="btn btn-std btn-xs"
                            :disabled="isContactReportResolving(report) || report.estado === 'aprobado'"
                            @click="openContactReportResolutionModal(report, 'aprobar_restituir')"
                          >
                            {{ isContactReportResolving(report) ? 'Procesando...' : 'Aprobar + restituir' }}
                          </button>
                          <button
                            type="button"
                            class="btn btn-std btn-xs btn-danger-light"
                            :disabled="isContactReportResolving(report) || report.estado === 'rechazado'"
                            @click="openContactReportResolutionModal(report, 'rechazar')"
                          >
                            Rechazar
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr v-else><td colspan="9">No hay reportes de contacto en esta página.</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="dashboard-section">
              <div class="section-header">
                <h3>Reembolsos Pendientes</h3>
                <div class="section-actions refund-actions">
                  <button type="button" class="btn btn-std" @click="openRefundCreateModal">
                    Crear reembolso
                  </button>
                  <div class="section-pagination-controls">
                    <button @click="changeSectionPage('reembolsosPendientes', -1)" :disabled="sectionPages.reembolsosPendientes <= 1 || sectionIsLoading.reembolsosPendientes || isInitialLoading">«</button>
                    <span>Pág. {{ sectionPages.reembolsosPendientes }}</span>
                    <button @click="changeSectionPage('reembolsosPendientes', 1)" :disabled="sectionIsLastPage.reembolsosPendientes || sectionIsLoading.reembolsosPendientes || isInitialLoading">»</button>
                  </div>
                </div>
              </div>
              <div class="table-container">
                <div v-if="sectionIsLoading.reembolsosPendientes" class="section-loader">Cargando...</div>
                <table v-else>
                  <thead><tr><th>ID</th><th>Cliente</th><th>Profesional</th><th>Orden</th><th>Monto</th><th>Estado</th><th>Antigüedad</th><th>Notificado</th><th>Datos</th><th>Acciones</th></tr></thead>
                  <tbody>
                    <tr v-if="reembolsosPendientes.length" v-for="refund in reembolsosPendientes" :key="'refund-'+refund.id">
                      <td>#{{ refund.id }}</td>
                      <td>
                        <button
                          v-if="refund.cliente && refund.cliente.id"
                          type="button"
                          class="dashboard-entity-link"
                          @click.stop="navigateToClient(refund.cliente.id)"
                        >
                          {{ refund.cliente && refund.cliente.nombre ? refund.cliente.nombre : '-' }}
                        </button>
                        <span v-else>{{ refund.cliente && refund.cliente.nombre ? refund.cliente.nombre : '-' }}</span>
                      </td>
                      <td>{{ [refund.profesional && refund.profesional.nombre, refund.profesional && refund.profesional.apellido].filter(Boolean).join(' ') || '-' }}</td>
                      <td>
                        <button
                          v-if="refund.orden && refund.orden.id"
                          type="button"
                          class="dashboard-entity-link"
                          @click.stop="navigateToOrder(refund.orden.id)"
                        >
                          #{{ refund.orden.id }}
                        </button>
                        <span v-else>-</span>
                      </td>
                      <td>${{ Number(refund.monto_total_cliente || 0).toLocaleString('es-CL') }}</td>
                      <td><span class="badge refund-badge">{{ formatRefundState(refund) }}</span></td>
                      <td>{{ formatDaysAgo(refund.antiguedad_dias) }}</td>
                      <td>{{ formatRefundNotification(refund) }}</td>
                      <td>{{ refund.datos_cliente_completos ? 'Completo' : 'Pendiente' }}</td>
                      <td>
                        <div class="refund-action-buttons">
                          <button type="button" class="btn btn-std btn-xs" @click="openRefundDetailModal(refund)">Ver</button>
                          <button type="button" class="btn btn-std btn-xs" @click="copyRefundLink(refund)">Link</button>
                          <button type="button" class="btn btn-std btn-xs" @click="resendRefundEmail(refund)" :disabled="refund.estado === 'paid' || refund.estado === 'canceled'">Reenviar</button>
                          <button type="button" class="btn btn-std btn-xs" @click="openRefundSettleModal(refund)" :disabled="!refund.datos_cliente_completos">Marcar pagado</button>
                          <button type="button" class="btn btn-std btn-xs btn-danger-light" @click="cancelRefund(refund)" :disabled="!refund.can_cancel">Cancelar</button>
                        </div>
                      </td>
                    </tr>
                    <tr v-else><td colspan="10">No hay reembolsos pendientes en esta página.</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Pagos y solicitudes de pago -->
             <div class="dashboard-section">
               <div class="section-header payments-section-header">
                 <div>
                   <h3>{{ paymentsPanelTab === 'pagos' ? 'Últimos Pagos Recibidos' : 'Solicitudes de Pago' }}</h3>
                   <div class="dashboard-tabs">
                     <button type="button" class="detail-tab-button" :class="{ active: paymentsPanelTab === 'pagos' }" @click="setPaymentsPanelTab('pagos')">Pagos recibidos</button>
                     <button type="button" class="detail-tab-button" :class="{ active: paymentsPanelTab === 'solicitudes' }" @click="setPaymentsPanelTab('solicitudes')">Solicitudes de pago</button>
                   </div>
                 </div>
                 <div v-if="paymentsPanelTab === 'pagos'" class="section-pagination-controls">
                   <button @click="changeSectionPage('ultimosPagos', -1)" :disabled="sectionPages.ultimosPagos <= 1 || sectionIsLoading.ultimosPagos || isInitialLoading">«</button>
                   <span>Pág. {{ sectionPages.ultimosPagos }}</span>
                   <button @click="changeSectionPage('ultimosPagos', 1)" :disabled="sectionIsLastPage.ultimosPagos || sectionIsLoading.ultimosPagos || isInitialLoading">»</button>
                 </div>
                 <div v-else class="section-pagination-controls">
                   <button @click="changeSectionPage('solicitudesPago', -1)" :disabled="sectionPages.solicitudesPago <= 1 || sectionIsLoading.solicitudesPago || isInitialLoading">«</button>
                   <span>Pág. {{ sectionPages.solicitudesPago }}</span>
                   <button @click="changeSectionPage('solicitudesPago', 1)" :disabled="sectionIsLastPage.solicitudesPago || sectionIsLoading.solicitudesPago || isInitialLoading">»</button>
                 </div>
               </div>
               <div class="table-container">
                 <div v-if="paymentsPanelTab === 'pagos'">
                   <div v-if="sectionIsLoading.ultimosPagos" class="section-loader">Cargando...</div>
                   <table v-else>
                     <thead><tr><th>Cliente</th><th>Profesional</th><th>Dirección</th><th>Fecha</th><th>Monto</th></tr></thead>
                     <tbody>
                       <tr v-if="ultimosPagos.length" v-for="pago in ultimosPagos" :key="'pago-'+pago.id">
                          <td>
                            <button
                              v-if="pago.cliente_id"
                              type="button"
                              class="dashboard-entity-link"
                              @click.stop="navigateToClient(pago.cliente_id)"
                            >
                              {{ pago.cliente_nombre }}
                            </button>
                            <span v-else>{{ pago.cliente_nombre }}</span>
                          </td>
                          <td>{{ pago.profesional_nombre }}</td><td>{{ pago.direccion }}</td><td>{{ formatDisplayDate(pago.fecha) }}</td><td>${{ pago.monto.toLocaleString('es-CL') }}</td>
                       </tr>
                       <tr v-else><td colspan="5">No hay pagos registrados en esta página.</td></tr>
                     </tbody>
                   </table>
                 </div>
                 <div v-else>
                   <div v-if="sectionIsLoading.solicitudesPago" class="section-loader">Cargando...</div>
                   <table v-else>
                     <thead>
                       <tr>
                         <th>ID</th>
                         <th>Cliente</th>
                         <th>Profesional</th>
                         <th>Fecha</th>
                         <th>Monto</th>
                         <th>Estado</th>
                         <th>Métodos</th>
                         <th>Link</th>
                       </tr>
                     </thead>
                     <tbody>
                       <tr v-if="solicitudesPago.length" v-for="solicitud in solicitudesPago" :key="'solicitud-pago-'+solicitud.id">
                         <td>#{{ solicitud.id }}</td>
                         <td>
                           <button
                             v-if="solicitud.cliente_id"
                             type="button"
                             class="dashboard-entity-link"
                             @click.stop="navigateToClient(solicitud.cliente_id)"
                           >
                             {{ solicitud.cliente_nombre }}
                           </button>
                           <span v-else>{{ solicitud.cliente_nombre }}</span>
                         </td>
                         <td>{{ solicitud.profesional_nombre }}</td>
                         <td>{{ formatDisplayDate(solicitud.fecha) }}</td>
                         <td>{{ formatCurrency(solicitud.monto) }}</td>
                         <td>
                           <span :class="['badge', 'pago-badge', getPagoEstadoClass(solicitud.pago_estado)]">
                             {{ solicitud.pago_estado_label || 'Sin pago' }}
                           </span>
                         </td>
                         <td>
                           <div class="payment-method-toggles">
                             <label class="payment-method-toggle payment-method-toggle--locked" title="Transbank está disponible por defecto para solicitudes con monto.">
                               <input type="checkbox" checked disabled>
                               <span>Transbank</span>
                             </label>
                             <label
                               class="payment-method-toggle"
                               :class="{ 'is-saving': isSolicitudPagoMethodUpdating(solicitud, 'mercado_pago'), 'payment-method-toggle--locked': isSolicitudPagoMethodLocked(solicitud, 'mercado_pago') }"
                               :title="isSolicitudPagoMethodLocked(solicitud, 'mercado_pago') ? 'Mercado Pago ya está disponible automáticamente para este monto.' : ''"
                             >
                               <input
                                 type="checkbox"
                                 :checked="isSolicitudPagoMethodEnabled(solicitud, 'mercado_pago')"
                                 :disabled="isSolicitudPagoMethodUpdating(solicitud, 'mercado_pago') || isSolicitudPagoMethodLocked(solicitud, 'mercado_pago')"
                                 @change="toggleSolicitudPagoMetodo(solicitud, 'mercado_pago', $event.target.checked)"
                               >
                               <span>Mercado Pago</span>
                             </label>
                             <label class="payment-method-toggle" :class="{ 'is-saving': isSolicitudPagoMethodUpdating(solicitud, 'khipu') }">
                               <input
                                 type="checkbox"
                                 :checked="isSolicitudPagoMethodEnabled(solicitud, 'khipu')"
                                 :disabled="isSolicitudPagoMethodUpdating(solicitud, 'khipu')"
                                 @change="toggleSolicitudPagoMetodo(solicitud, 'khipu', $event.target.checked)"
                               >
                               <span>Khipu</span>
                             </label>
                           </div>
                         </td>
                         <td>
                           <button type="button" class="btn btn-std btn-xs" @click="copySolicitudPagoLink(solicitud)">Copiar</button>
                         </td>
                       </tr>
                       <tr v-else><td colspan="8">No hay solicitudes de pago en esta página.</td></tr>
                     </tbody>
                   </table>
                 </div>
               </div>
             </div>
             <!-- End Pagos -->

             <!-- Ordenes en Curso -->
             <div class="dashboard-section">
               <div class="section-header">
                 <h3>Órdenes en Curso</h3>
                 <div class="section-pagination-controls">
                   <button @click="changeSectionPage('ordenesEnCurso', -1)" :disabled="sectionPages.ordenesEnCurso <= 1 || sectionIsLoading.ordenesEnCurso || isInitialLoading">«</button>
                   <span>Pág. {{ sectionPages.ordenesEnCurso }}</span>
                   <button @click="changeSectionPage('ordenesEnCurso', 1)" :disabled="sectionIsLastPage.ordenesEnCurso || sectionIsLoading.ordenesEnCurso || isInitialLoading">»</button>
                 </div>
               </div>
               <div class="table-container">
                 <div v-if="sectionIsLoading.ordenesEnCurso" class="section-loader">Cargando...</div>
                 <table v-else>
                   <thead><tr><th>ID Orden</th><th>Cliente</th><th>Profesional</th><th>Servicio</th><th>Estado</th><th>Fecha Inicio</th></tr></thead>
                   <tbody>
                      <tr v-if="ordenesEnCurso.length" v-for="orden in ordenesEnCurso" :key="'ord-'+orden.id_orden">
                        <td>
                          <button
                            v-if="orden.id_orden"
                            type="button"
                            class="dashboard-entity-link"
                            @click.stop="navigateToOrder(orden.id_orden)"
                          >
                            #{{ orden.id_orden }}
                          </button>
                          <span v-else>-</span>
                        </td>
                        <td>
                          <button
                            v-if="orden.cliente_id"
                            type="button"
                            class="dashboard-entity-link"
                            @click.stop="navigateToClient(orden.cliente_id)"
                          >
                            {{ orden.cliente_nombre }}
                          </button>
                          <span v-else>{{ orden.cliente_nombre }}</span>
                        </td>
                        <td>{{ orden.profesional_nombre }}</td><td>{{ orden.servicio }}</td>
                       <td><span :class="['badge', 'status-badge', getStatusClass(orden.estado)]">{{ orden.estado }}</span></td><td>{{ formatDisplayDate(orden.fecha_inicio) }}</td>
                     </tr>
                     <tr v-else><td colspan="6">No hay órdenes en curso en esta página.</td></tr>
                   </tbody>
                 </table>
               </div>
             </div>
             <!-- End Ordenes -->

             <!-- Últimas Calificaciones -->
             <div class="dashboard-section">
               <div class="section-header">
                 <h3>Últimas Calificaciones</h3>
                 <div class="section-pagination-controls">
                   <button @click="changeSectionPage('ultimasCalificaciones', -1)" :disabled="sectionPages.ultimasCalificaciones <= 1 || sectionIsLoading.ultimasCalificaciones || isInitialLoading">«</button>
                   <span>Pág. {{ sectionPages.ultimasCalificaciones }}</span>
                   <button @click="changeSectionPage('ultimasCalificaciones', 1)" :disabled="sectionIsLastPage.ultimasCalificaciones || sectionIsLoading.ultimasCalificaciones || isInitialLoading">»</button>
                 </div>
               </div>
               <div class="table-container">
                 <div v-if="sectionIsLoading.ultimasCalificaciones" class="section-loader">Cargando...</div>
                 <table v-else>
                   <thead><tr><th>Cliente</th><th>Profesional</th><th>Calificación</th><th>Comentario</th><th>Fecha</th></tr></thead>
                   <tbody>
                     <tr v-if="ultimasCalificaciones.length" v-for="calificacion in ultimasCalificaciones" :key="'cal-'+calificacion.id">
                        <td>
                          <button
                            v-if="calificacion.cliente_id"
                            type="button"
                            class="dashboard-entity-link"
                            @click.stop="navigateToClient(calificacion.cliente_id)"
                          >
                            {{ calificacion.cliente_nombre }}
                          </button>
                          <span v-else>{{ calificacion.cliente_nombre }}</span>
                        </td>
                        <td>{{ calificacion.profesional_nombre }}</td><td>{{ '⭐'.repeat(calificacion.calificacion) }} ({{ calificacion.calificacion }}/5)</td>
                       <td>"{{ calificacion.comentario }}"</td><td>{{ formatDisplayDate(calificacion.fecha) }}</td>
                     </tr>
                     <tr v-else><td colspan="5">No hay calificaciones en esta página.</td></tr>
                   </tbody>
                 </table>
               </div>
             </div>
             <!-- End Calificaciones -->

          </div> <!-- End Dashboard Grid -->
        </div>

      </div> <!-- End v-if for admin_datos check -->

      <!-- 2. Show user loading message IF admin_datos is not yet loaded -->
      <div v-else-if="!admin_datos" class="main-container loading-indicator">
         <p>Cargando datos de usuario...</p>
      </div>

      <!-- 3. Show unauthorized message IF admin_datos loaded BUT user is not superadmin -->
      <div v-else class="main-container">
         <p>Acceso no autorizado.</p>
      </div>
       <!-- === End Authorization Check === -->

      <transition name="modal-fade">
        <div
          v-if="showContactReportResolutionModal && contactReportResolutionTarget"
          class="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-report-resolution-modal-title"
          @click.self="closeContactReportResolutionModal"
        >
          <div class="modal-container contact-report-resolution-modal">
            <header class="modal-header">
              <h3 id="contact-report-resolution-modal-title">{{ contactReportResolutionTitle }}</h3>
              <button
                type="button"
                class="modal-close"
                aria-label="Cerrar"
                :disabled="contactReportResolutionSubmitting"
                @click="closeContactReportResolutionModal"
              >
                ×
              </button>
            </header>
            <div class="modal-content">
              <div :class="['contact-report-resolution-panel', contactReportResolutionToneClass]">
                <div class="contact-report-resolution-kicker">
                  {{ contactReportResolutionIsApprove ? 'Restitución de puntos' : 'Revisión sin restitución' }}
                </div>
                <p v-if="contactReportResolutionIsApprove">
                  Se aprobará el reporte, se restituirán {{ contactReportResolutionPointsLabel }} al profesional y se le enviará un correo avisando la aprobación.
                </p>
                <p v-else>
                  Se rechazará el reporte y se le enviará un correo al profesional explicando que no corresponde restitución.
                </p>
              </div>

              <div class="contact-report-resolution-summary">
                <div>
                  <span>Solicitud</span>
                  <strong>{{ contactReportResolutionTargetTitle }}</strong>
                </div>
                <div>
                  <span>Profesional</span>
                  <strong>{{ contactReportResolutionProfessionalName }}</strong>
                </div>
                <div>
                  <span>Motivo reportado</span>
                  <strong>{{ contactReportResolutionTarget.motivo_label || contactReportResolutionTarget.motivo || '-' }}</strong>
                </div>
                <div>
                  <span>Puntos</span>
                  <strong>{{ contactReportResolutionPointsLabel }}</strong>
                </div>
              </div>

              <div v-if="contactReportResolutionTarget.detalle" class="contact-report-resolution-detail">
                <span>Detalle del profesional</span>
                <p>{{ contactReportResolutionTarget.detalle }}</p>
              </div>

              <div class="form-row">
                <label>Comentario para el profesional y registro interno</label>
                <textarea
                  v-model.trim="contactReportResolutionNotes"
                  rows="4"
                  :placeholder="contactReportResolutionIsApprove ? 'Ej: Validamos que el número no corresponde y restituimos tus puntos.' : 'Ej: Revisamos los datos y estaban correctamente ingresados; el cliente no respondió.'"
                ></textarea>
                <small class="meta-muted">
                  Este comentario queda guardado en el reporte y se incluye en el correo al profesional.
                </small>
              </div>
            </div>
            <footer class="modal-footer">
              <button
                type="button"
                class="btn btn-cancel"
                :disabled="contactReportResolutionSubmitting"
                @click="closeContactReportResolutionModal"
              >
                Cancelar
              </button>
              <button
                type="button"
                :class="['btn', contactReportResolutionIsApprove ? 'btn-primary' : 'btn-danger-light']"
                :disabled="contactReportResolutionSubmitting"
                @click="submitContactReportResolution"
              >
                {{ contactReportResolutionSubmitLabel }}
              </button>
            </footer>
          </div>
        </div>
      </transition>

      <transition name="modal-fade">
        <div
          v-if="showRefundCreateModal"
          class="modal-overlay"
          role="dialog"
          aria-modal="true"
          @click.self="closeRefundCreateModal"
        >
          <div class="modal-container refund-modal-container">
            <header class="modal-header">
              <h3>Crear reembolso</h3>
              <button type="button" class="modal-close" aria-label="Cerrar" :disabled="refundCreateSubmitting" @click="closeRefundCreateModal">×</button>
            </header>
            <div class="modal-content">
              <div class="form-row">
                <label>ID Pago</label>
                <input v-model="newRefund.pago_id" type="number" min="1" />
              </div>
              <div class="form-row">
                <label>Monto a reembolsar</label>
                <input v-model="newRefund.monto" type="number" min="1" />
              </div>
              <div class="form-row">
                <label>Motivo</label>
                <input v-model="newRefund.motivo" type="text" />
              </div>
              <div class="form-row">
                <label>Notas internas</label>
                <textarea v-model="newRefund.notas_internas" rows="4"></textarea>
              </div>
              <div class="form-row inline-checkbox">
                <label>
                  <input v-model="newRefund.send_email" type="checkbox" />
                  Enviar correo al cliente al crear
                </label>
              </div>
            </div>
            <footer class="modal-footer">
              <button type="button" class="btn btn-light" :disabled="refundCreateSubmitting" @click="closeRefundCreateModal">Cancelar</button>
              <button type="button" class="btn btn-primary" :disabled="refundCreateSubmitting" @click="submitRefundCreate">
                {{ refundCreateSubmitting ? 'Guardando...' : 'Crear reembolso' }}
              </button>
            </footer>
          </div>
        </div>
      </transition>

      <transition name="modal-fade">
        <div
          v-if="showRefundDetailModal && selectedRefund"
          class="modal-overlay"
          role="dialog"
          aria-modal="true"
          @click.self="closeRefundDetailModal"
        >
          <div class="modal-container refund-modal-container">
            <header class="modal-header">
              <h3>Reembolso #{{ selectedRefund.id }}</h3>
              <button type="button" class="modal-close" aria-label="Cerrar" @click="closeRefundDetailModal">×</button>
            </header>
            <div class="modal-content refund-detail-grid">
              <p><strong>Estado:</strong> {{ selectedRefund.estado_label }}</p>
              <p><strong>Monto cliente:</strong> ${{ Number(selectedRefund.monto_total_cliente || 0).toLocaleString('es-CL') }}</p>
              <p><strong>Descuento profesional:</strong> ${{ Number(selectedRefund.monto_total_descuento_profesional || 0).toLocaleString('es-CL') }}</p>
              <p><strong>Email cliente:</strong> {{ selectedRefund.email_cliente || '-' }}</p>
              <p><strong>Motivo:</strong> {{ selectedRefund.motivo || '-' }}</p>
              <p><strong>Notas internas:</strong> {{ selectedRefund.notas_internas || '-' }}</p>
              <p><strong>Titular:</strong> {{ selectedRefund.datos_cliente && selectedRefund.datos_cliente.nombre_titular ? selectedRefund.datos_cliente.nombre_titular : '-' }}</p>
              <p><strong>RUT:</strong> {{ selectedRefund.datos_cliente && selectedRefund.datos_cliente.rut_titular ? selectedRefund.datos_cliente.rut_titular : '-' }}</p>
              <p><strong>Banco:</strong> {{ selectedRefund.datos_cliente && selectedRefund.datos_cliente.banco_nombre ? selectedRefund.datos_cliente.banco_nombre : '-' }}</p>
              <p><strong>Tipo cuenta:</strong> {{ selectedRefund.datos_cliente && selectedRefund.datos_cliente.tipo_cuenta_nombre ? selectedRefund.datos_cliente.tipo_cuenta_nombre : '-' }}</p>
              <p><strong>Cuenta:</strong> {{ selectedRefund.datos_cliente && selectedRefund.datos_cliente.cuenta_bancaria ? selectedRefund.datos_cliente.cuenta_bancaria : '-' }}</p>
              <p><strong>Email datos:</strong> {{ selectedRefund.datos_cliente && selectedRefund.datos_cliente.email ? selectedRefund.datos_cliente.email : '-' }}</p>
              <p><strong>Link público:</strong> <a :href="selectedRefund.public_link" target="_blank">Abrir</a></p>
            </div>
          </div>
        </div>
      </transition>

      <transition name="modal-fade">
        <div
          v-if="showRefundSettleModal && selectedRefund"
          class="modal-overlay"
          role="dialog"
          aria-modal="true"
          @click.self="closeRefundSettleModal"
        >
          <div class="modal-container refund-modal-container">
            <header class="modal-header">
              <h3>Marcar reembolso pagado</h3>
              <button type="button" class="modal-close" aria-label="Cerrar" :disabled="refundSettleSubmitting" @click="closeRefundSettleModal">×</button>
            </header>
            <div class="modal-content">
              <p class="meta-muted">Reembolso #{{ selectedRefund.id }} · ${{ Number(selectedRefund.monto_total_cliente || 0).toLocaleString('es-CL') }}</p>
              <div class="form-row">
                <label>Notas del pago</label>
                <textarea v-model="refundSettleForm.pago_notas" rows="4"></textarea>
              </div>
              <div class="form-row">
                <label>Comprobante</label>
                <input type="file" accept="image/*,application/pdf" @change="onRefundSettleFileChange" />
              </div>
            </div>
            <footer class="modal-footer">
              <button type="button" class="btn btn-light" :disabled="refundSettleSubmitting" @click="closeRefundSettleModal">Cancelar</button>
              <button type="button" class="btn btn-primary" :disabled="refundSettleSubmitting" @click="submitRefundSettle">
                {{ refundSettleSubmitting ? 'Guardando...' : 'Marcar pagado' }}
              </button>
            </footer>
          </div>
        </div>
      </transition>

      <transition name="modal-fade">
        <div
          v-if="showVisitaDeleteModal && visitaToDelete"
          class="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-visita-delete-modal-title"
          @click.self="closeVisitaDeleteModal"
        >
          <div class="modal-container">
            <header class="modal-header">
              <h3 id="dashboard-visita-delete-modal-title">Confirmar eliminación</h3>
              <button
                type="button"
                class="modal-close"
                aria-label="Cerrar"
                :disabled="isVisitaRemoving(visitaToDelete.id)"
                @click="closeVisitaDeleteModal"
              >
                ×
              </button>
            </header>
            <div class="modal-content">
              <p>
                ¿Seguro que deseas eliminar esta visita?
              </p>
              <p class="meta-muted">
                {{ formatDisplayDate(visitaToDelete.fecha, false) }} {{ visitaToDelete.hora || '' }}
                · {{ visitaToDelete.cliente_nombre || 'Cliente' }}
              </p>
            </div>
            <footer class="modal-footer">
              <button
                type="button"
                class="btn btn-cancel"
                :disabled="isVisitaRemoving(visitaToDelete.id)"
                @click="closeVisitaDeleteModal"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="isVisitaRemoving(visitaToDelete.id)"
                @click="confirmVisitaDelete"
              >
                {{ isVisitaRemoving(visitaToDelete.id) ? 'Eliminando...' : 'Eliminar' }}
              </button>
            </footer>
          </div>
        </div>
      </transition>

    </div> <!-- End column-right -->
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import Fuse from 'fuse.js';
import SideNav from '@/components/SideNav.vue';
import TopNav from '@/components/TopNav.vue';
import ReviewReports from '@/components/ReviewReports.vue';

const SECTION_KEYS = [
  'solicitudesCompradas',
  'contactReports',
  'reembolsosPendientes',
  'proximasVisitas',
  'presupuestosPendientesEnviar',
  'jobbingB2B',
  'solicitudesSinRespuesta',
  'ultimosPagos',
  'solicitudesPago',
  'ordenesEnCurso',
  'ultimasCalificaciones',
  'warmConversations',
];

// Helper function to create initial state objects
const createInitialState = (defaultValue) => {
  const state = {};
  SECTION_KEYS.forEach(key => {
    state[key] = defaultValue;
  });
  return state;
};

export default {
  name: 'AdminDashboard',
  props: ['id'],
  components: { SideNav, TopNav, ReviewReports },
  data() {
    return {
      isInitialLoading: true, // For the very first data load
      errorLoading: null, // For global API errors
      limitPerPage: 20, // Match backend limit
      useFullWidthTables: false,

      // Store page number and last page status PER section
      sectionPages: createInitialState(1),
      sectionIsLastPage: createInitialState(false),
      sectionIsLoading: createInitialState(false),

      // Data arrays
      solicitudesCompradas: [],
      comprasSearch: '',
      comprasDesde: '',
      comprasHasta: '',
      comprasAppliedFilters: {},
      comprasTotal: 0,
      comprasTotalPuntos: 0,
      comprasTotalPages: 1,
      comprasError: null,
      contactReports: [],
      contactReportsStatusFilter: 'pendiente',
      contactReportsResolving: {},
      showContactReportResolutionModal: false,
      contactReportResolutionTarget: null,
      contactReportResolutionAction: '',
      contactReportResolutionNotes: '',
      reembolsosPendientes: [],
      proximasVisitas: [],
      editingVisitaId: null,
      visitaEditDraft: {
        fecha: '',
        hora: '',
      },
      visitasUpdating: [],
      visitasRemoving: [],
      showVisitaDeleteModal: false,
      visitaToDelete: null,
      presupuestosPendientesEnviar: [],
      solicitudesSinRespuesta: [],
      ultimosPagos: [],
      solicitudesPago: [],
      ordenesEnCurso: [],
      ultimasCalificaciones: [],
      warmConversations: [],
      jobbingB2BRequests: [],
      empresasAdministracion: [],
      jobbingB2BEmpresaFilters: [],
      jobbingB2BEmpresaSelection: '',
      jobbingB2BEstadoFilters: [],
      jobbingB2BEstadoSelection: '',
      jobbingB2BFromDate: this.getDefaultJobbingB2BFromDate(),
      jobbingB2BWarningFilter: 'all',
      jobbingB2BSortKey: 'fecha_creado',
      jobbingB2BSortOrder: 'desc',
      jobbingB2BExpanded: [],

      fProfesionales: null,

      profesionalPendienteQuery: '',
      resultadoProfesionalesPendientes: [],
      profesionalPendienteSeleccionado: null,
      profesionales: [],
      presupuestosPendientesRemoving: [],
      solicitudesTipoFiltro: 'Todas',
      paymentsPanelTab: 'pagos',
      solicitudPagoMethodUpdating: {},
      warmProfessionalQuery: '',
      warmProfessionalResults: [],
      warmProfessionalFilters: [],
      warmInterestFilters: [],
      warmStartDate: this.getDefaultWarmStartDate(),
      onlyClientLastMessage: true,
      warmSortKey: 'flagged_at',
      warmSortOrder: 'desc',
      expandedWarmConversationId: null,
      warmConversationMessages: {},
      warmConversationLoading: false,
      warmConversationError: null,

      dashboardIsRefreshing: false,
      lastDashboardRefreshAt: null,
      dashboardNow: Date.now(),
      dashboardRefreshAfterInactiveMs: 2 * 60 * 1000,
      dashboardDeactivatedAt: null,
      dashboardRelativeTimer: null,

      showRefundCreateModal: false,
      refundCreateSubmitting: false,
      newRefund: {
        pago_id: '',
        monto: '',
        motivo: '',
        notas_internas: '',
        send_email: true,
      },
      showRefundDetailModal: false,
      selectedRefund: null,
      showRefundSettleModal: false,
      refundSettleSubmitting: false,
      refundSettleForm: {
        pago_notas: '',
        file: null,
      },

    }
  },
  created() {
    // Fetch initial data only if authorized (or wait for watcher)
    if (this.admin_datos && this.admin_datos.superadmin) {
      this.fetchDashboardData();
      this.loadProfesionales();
      this.loadEmpresasAdministracion();
    }
    // this.initialize(); // Uncomment if needed from vuex actions
  },
  methods: {
    ...mapActions(['initialize']),

    navigateToClient(clientId) {
      if (!clientId) return;
      this.$router.push(`/superadmin/Usuarios/${clientId}`).catch(() => {});
    },

    navigateToOrder(orderId) {
      if (!orderId) return;
      this.$router.push(`/OrdenDetalleAdmin/${orderId}`).catch(() => {});
    },

    navigateToSolicitud(solicitudId) {
      if (!solicitudId) return;
      this.$router.push(`/SolicitudDetalleAdmin/${solicitudId}`).catch(() => {});
    },

    navigateToProfessional(profesionalId) {
      if (!profesionalId) return;
      this.$router.push(`/VerProfesional/${profesionalId}`).catch(() => {});
    },

    startDashboardRelativeTimer() {
      this.dashboardNow = Date.now();
      if (this.dashboardRelativeTimer) return;
      this.dashboardRelativeTimer = setInterval(() => {
        this.dashboardNow = Date.now();
      }, 60 * 1000);
    },

    stopDashboardRelativeTimer() {
      if (!this.dashboardRelativeTimer) return;
      clearInterval(this.dashboardRelativeTimer);
      this.dashboardRelativeTimer = null;
    },

    shouldRefreshDashboardAfterInactive() {
      if (!this.dashboardDeactivatedAt) return false;
      return Date.now() - this.dashboardDeactivatedAt >= this.dashboardRefreshAfterInactiveMs;
    },

    refreshDashboardManually() {
      if (this.dashboardRefreshButtonDisabled) return;
      this.fetchDashboardData();
    },

    async fetchDashboardData() {
      // Don't reset section loaders here, they are set by changeSectionPage
      this.errorLoading = null; // Reset global error on new fetch
      this.dashboardIsRefreshing = true;

      const params = new URLSearchParams();
      params.append('refunds_page', this.sectionPages.reembolsosPendientes);
      params.append('visitas_page', this.sectionPages.proximasVisitas);
      params.append('pendientes_page', this.sectionPages.presupuestosPendientesEnviar);
      params.append('solicitudes_page', this.sectionPages.solicitudesSinRespuesta);
      params.append('pagos_page', this.sectionPages.ultimosPagos);
      params.append('solicitudes_pago_page', this.sectionPages.solicitudesPago);
      params.append('ordenes_page', this.sectionPages.ordenesEnCurso);
      params.append('calificaciones_page', this.sectionPages.ultimasCalificaciones);
      params.append('jobbing_b2b_from_date', this.jobbingB2BFromDate || this.getDefaultJobbingB2BFromDate());
      params.append('warm_start_date', this.warmStartDate || this.getDefaultWarmStartDate());
      if (this.profesionalPendienteSeleccionado) {
        params.append('pendientes_profesional_id', this.profesionalPendienteSeleccionado.id);
      }

      const apiUrl = `${window.hostname}/api/superadmin/dashboard/section_paged?${params.toString()}`;

      try {
        const response = await axios.get(apiUrl);
        if (response && response.data) {
          const data = response.data;

          // Update data arrays
          this.reembolsosPendientes = data.reembolsos_pendientes || [];
          this.proximasVisitas = data.proximas_visitas || [];
          this.presupuestosPendientesEnviar = data.presupuestos_pendientes_enviar || [];
          this.solicitudesSinRespuesta = data.solicitudes_sin_respuesta || [];
          this.ultimosPagos = data.ultimos_pagos || [];
          this.solicitudesPago = data.solicitudes_pago || [];
          this.ordenesEnCurso = data.ordenes_en_curso || [];
          this.ultimasCalificaciones = data.ultimas_calificaciones || [];
          this.warmConversations = data.warm_conversations || [];
          this.jobbingB2BRequests = (data.jobbing_b2b || []).map((sol) => ({
            ...sol,
            empresa_id: sol && sol.empresa_id != null ? Number(sol.empresa_id) : sol?.empresa_id,
          }));
          const validJobbingIds = new Set(this.jobbingB2BRequests.map(s => s.id));
          this.jobbingB2BExpanded = this.jobbingB2BExpanded.filter(id => validJobbingIds.has(id));
          this.sortJobbingB2BRequests();

          // Update 'isLastPage' status for EACH section
          this.sectionIsLastPage.reembolsosPendientes = this.reembolsosPendientes.length < this.limitPerPage;
          this.sectionIsLastPage.proximasVisitas = this.proximasVisitas.length < this.limitPerPage;
          this.sectionIsLastPage.presupuestosPendientesEnviar = this.presupuestosPendientesEnviar.length < this.limitPerPage;
          this.sectionIsLastPage.solicitudesSinRespuesta = this.solicitudesSinRespuesta.length < this.limitPerPage;
          this.sectionIsLastPage.ultimosPagos = this.ultimosPagos.length < this.limitPerPage;
          this.sectionIsLastPage.solicitudesPago = this.solicitudesPago.length < this.limitPerPage;
          this.sectionIsLastPage.ordenesEnCurso = this.ordenesEnCurso.length < this.limitPerPage;
          this.sectionIsLastPage.ultimasCalificaciones = this.ultimasCalificaciones.length < this.limitPerPage;
          this.sectionIsLastPage.jobbingB2B = true;
          this.sectionIsLastPage.warmConversations = false;
          await Promise.all([this.fetchContactReports({ setLoading: false }), this.fetchSolicitudesCompradas()]);
          const refreshedAt = Date.now();
          this.lastDashboardRefreshAt = refreshedAt;
          this.dashboardNow = refreshedAt;

        } else {
          throw new Error("Respuesta inválida desde el servidor");
        }
      } catch (error) {
        console.error("Error fetching section-paginated dashboard data:", error);
        this.errorLoading = error.response?.data?.error || error.message || "Ocurrió un error desconocido.";
      } finally {
        this.isInitialLoading = false; // Mark initial load as complete
        // Reset ALL section loading flags after any fetch completes
        this.sectionIsLoading = createInitialState(false);
        this.dashboardIsRefreshing = false;
      }
    },

    applyComprasFilters() {
      this.comprasAppliedFilters = { q: this.comprasSearch, desde: this.comprasDesde, hasta: this.comprasHasta };
      this.sectionPages.solicitudesCompradas = 1;
      return this.fetchSolicitudesCompradas();
    },

    clearComprasFilters() {
      this.comprasSearch = '';
      this.comprasDesde = '';
      this.comprasHasta = '';
      return this.applyComprasFilters();
    },

    async fetchSolicitudesCompradas() {
      this.sectionIsLoading.solicitudesCompradas = true;
      this.comprasError = null;
      try {
        const { data } = await axios.get(`${window.hostname}api/superadmin/dashboard/solicitudes-compradas`, {
          params: { ...this.comprasAppliedFilters, page: this.sectionPages.solicitudesCompradas },
        });
        this.solicitudesCompradas = data.items || [];
        this.comprasTotal = data.total;
        this.comprasTotalPuntos = data.total_puntos;
        this.comprasTotalPages = data.total_pages;
        this.sectionPages.solicitudesCompradas = data.page;
        this.sectionIsLastPage.solicitudesCompradas = !data.has_more;
      } catch (error) {
        this.comprasError = error.response?.data?.message || 'No se pudieron cargar las compras.';
      } finally {
        this.sectionIsLoading.solicitudesCompradas = false;
      }
    },

    async fetchContactReports({ setLoading = true } = {}) {
      if (setLoading) {
        this.$set(this.sectionIsLoading, 'contactReports', true);
      }
      const params = new URLSearchParams();
      params.append('estado', this.contactReportsStatusFilter || 'pendiente');
      params.append('page', this.sectionPages.contactReports || 1);
      params.append('limit', this.limitPerPage);

      try {
        const response = await axios.get(`${window.hostname}api/admin/jobbing_points/contact_reports?${params.toString()}`);
        const reports = response && response.data && Array.isArray(response.data.reportes)
          ? response.data.reportes
          : [];
        this.contactReports = reports;
        this.$set(this.sectionIsLastPage, 'contactReports', reports.length < this.limitPerPage);
      } catch (error) {
        console.error('Error fetching contact reports:', error);
        this.contactReports = [];
        this.$set(this.sectionIsLastPage, 'contactReports', true);
        if (setLoading) {
          window.alert((error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) || 'No se pudieron cargar los reportes de contacto.');
        }
      } finally {
        if (setLoading) {
          this.$set(this.sectionIsLoading, 'contactReports', false);
        }
      }
    },

    async loadProfesionales() {
      try {
        const resp = await axios.get(`${window.hostname}api/admin/getprofesionales`);
        const list = resp.data.profesionales || [];
        this.profesionales = list.map(p => {
          const pro = p.profesional || p;
          return { id: pro.id, nombre: `${pro.nombre} ${pro.apellido}`.trim() };
        });
        this.fProfesionales = new Fuse(this.profesionales, { keys: ['nombre'] });
      } catch (e) {
        console.error('Error loading professionals', e);
      }
    },

    async loadEmpresasAdministracion() {
      try {
        const resp = await axios.get(`${window.hostname}api/admin/get_empresas_administracion`);
        this.empresasAdministracion = resp.data?.empresas || [];
      } catch (e) {
        console.error('Error loading empresas de administración', e);
      }
    },

    selectProfesionalPendiente(p) {
      this.profesionalPendienteSeleccionado = p;
      this.profesionalPendienteQuery = '';
      this.resultadoProfesionalesPendientes = [];
      this.sectionPages.presupuestosPendientesEnviar = 1;
      this.fetchDashboardData();
    },

    clearProfesionalPendienteFilter() {
      this.profesionalPendienteSeleccionado = null;
      this.resultadoProfesionalesPendientes = [];
      this.profesionalPendienteQuery = '';
      this.sectionPages.presupuestosPendientesEnviar = 1;
      this.fetchDashboardData();
    },

    changeSectionPage(sectionKey, direction) {
       // Prevent changes during initial load or if already loading that section
       if (this.isInitialLoading || this.sectionIsLoading[sectionKey]) return;

      const currentPage = this.sectionPages[sectionKey];
      const nextPage = currentPage + direction;
      const isLastPage = sectionKey === 'warmConversations'
        ? this.warmIsLastPage
        : this.sectionIsLastPage[sectionKey];

      if (direction > 0 && isLastPage) return;
      if (direction < 0 && currentPage <= 1) return;

      this.sectionPages[sectionKey] = nextPage;

      if (sectionKey === 'solicitudesCompradas') {
        this.fetchSolicitudesCompradas();
        return;
      }
      if (sectionKey === 'warmConversations') {
        return; // Client-side pagination only
      }
      if (sectionKey === 'contactReports') {
        this.fetchContactReports();
        return;
      }

      // Set loading state *only* for the changing section
      this.sectionIsLoading[sectionKey] = true;
      this.errorLoading = null; // Clear global error when trying a section change
      this.fetchDashboardData(); // Refetch all data
    },

    setPaymentsPanelTab(tab) {
      this.paymentsPanelTab = tab === 'solicitudes' ? 'solicitudes' : 'pagos';
    },

    solicitudPagoMethodKey(solicitud, metodo) {
      return `${solicitud && solicitud.id ? solicitud.id : 'unknown'}:${metodo}`;
    },

    isSolicitudPagoMethodUpdating(solicitud, metodo) {
      return !!this.solicitudPagoMethodUpdating[this.solicitudPagoMethodKey(solicitud, metodo)];
    },

    isSolicitudPagoMethodEnabled(solicitud, metodo) {
      const methods = solicitud && solicitud.payment_methods ? solicitud.payment_methods : {};
      return !!methods[metodo];
    },

    isSolicitudPagoMethodLocked(solicitud, metodo) {
      if (metodo !== 'mercado_pago') return false;
      const enabled = this.isSolicitudPagoMethodEnabled(solicitud, metodo);
      const manualMethods = solicitud && solicitud.metodos_pago_habilitados ? solicitud.metodos_pago_habilitados : {};
      return enabled && !manualMethods[metodo];
    },

    async toggleSolicitudPagoMetodo(solicitud, metodo, enabled) {
      if (!solicitud || !solicitud.id || this.isSolicitudPagoMethodLocked(solicitud, metodo)) return;
      const key = this.solicitudPagoMethodKey(solicitud, metodo);
      this.$set(this.solicitudPagoMethodUpdating, key, true);
      try {
        const response = await axios.patch(`${window.hostname}api/admin/solicitud_pago/${solicitud.id}/metodos_pago`, {
          metodos_pago_habilitados: {
            [metodo]: enabled,
          },
        });
        const updatedSolicitud = response && response.data ? response.data.solicitud : null;
        const updatedMethods = updatedSolicitud && updatedSolicitud.metodos_pago_habilitados
          ? updatedSolicitud.metodos_pago_habilitados
          : {
              ...(solicitud.metodos_pago_habilitados || {}),
              [metodo]: enabled,
            };
        this.$set(solicitud, 'metodos_pago_habilitados', updatedMethods);
        this.$set(solicitud, 'payment_methods', {
          ...(solicitud.payment_methods || {}),
          [metodo]: metodo === 'mercado_pago'
            ? this.resolveMercadoPagoDisponibilidad(solicitud, updatedMethods)
            : (Number(solicitud && solicitud.monto) || 0) > 0 && !!updatedMethods[metodo],
        });
      } catch (error) {
        window.alert((error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) || 'No se pudo actualizar el método de pago.');
      } finally {
        this.$delete(this.solicitudPagoMethodUpdating, key);
      }
    },

    resolveMercadoPagoDisponibilidad(solicitud, metodos) {
      const amount = Number(solicitud && solicitud.monto) || 0;
      return amount > 0 && (amount <= 20000 || !!(metodos && metodos.mercado_pago));
    },

    async copySolicitudPagoLink(solicitud) {
      if (!solicitud || !solicitud.public_link) return;
      try {
        await navigator.clipboard.writeText(solicitud.public_link);
      } catch (error) {
        window.prompt('Copia el link de la solicitud de pago:', solicitud.public_link);
      }
    },

    changeWarmSort(key) {
      if (!key) return;
      if (this.warmSortKey === key) {
        this.warmSortOrder = this.warmSortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.warmSortKey = key;
        this.warmSortOrder = 'desc';
      }
      this.resetWarmPagination();
    },

    toggleFullWidthTables() {
      this.useFullWidthTables = !this.useFullWidthTables;
    },

    handleContactReportsStatusChange() {
      this.sectionPages.contactReports = 1;
      this.fetchContactReports();
    },

    getContactReportSnapshotValue(report, key) {
      const snapshot = report && report.contacto_snapshot ? report.contacto_snapshot : {};
      const value = snapshot[key];
      return value === null || value === undefined ? '' : String(value).trim();
    },

    contactReportStatusLabel(report) {
      const estado = report && report.estado ? String(report.estado) : '';
      const labels = {
        pendiente: 'Pendiente',
        aprobado: 'Aprobado',
        rechazado: 'Rechazado',
        resuelto: 'Resuelto',
      };
      return labels[estado] || estado || '-';
    },

    contactReportStatusClass(report) {
      const estado = report && report.estado ? String(report.estado) : '';
      if (estado === 'aprobado') return 'contact-report-status-approved';
      if (estado === 'rechazado') return 'contact-report-status-rejected';
      if (estado === 'resuelto') return 'contact-report-status-resolved';
      return 'contact-report-status-pending';
    },

    contactReportRowClass(report) {
      return report && report.estado === 'pendiente' ? 'contact-report-row-pending' : '';
    },

    contactReportResolveKey(report) {
      return report && report.id ? String(report.id) : '';
    },

    isContactReportResolving(report) {
      const key = this.contactReportResolveKey(report);
      return Boolean(key && this.contactReportsResolving[key]);
    },

    openContactReportResolutionModal(report, action) {
      if (!report || !report.id || this.isContactReportResolving(report)) return false;
      const approve = action === 'aprobar_restituir';
      if (approve && report.estado === 'aprobado') return false;
      if (!approve && report.estado === 'rechazado') return false;
      this.contactReportResolutionTarget = report;
      this.contactReportResolutionAction = action;
      this.contactReportResolutionNotes = '';
      this.showContactReportResolutionModal = true;
    },

    closeContactReportResolutionModal(force = false) {
      if (!force && this.contactReportResolutionSubmitting) return;
      this.showContactReportResolutionModal = false;
      this.contactReportResolutionTarget = null;
      this.contactReportResolutionAction = '';
      this.contactReportResolutionNotes = '';
    },

    async submitContactReportResolution() {
      if (!this.contactReportResolutionTarget || !this.contactReportResolutionAction) return;
      const ok = await this.resolveContactReport(
        this.contactReportResolutionTarget,
        this.contactReportResolutionAction,
        this.contactReportResolutionNotes,
      );
      if (ok) {
        this.closeContactReportResolutionModal(true);
      }
    },

    async resolveContactReport(report, action, notes = '') {
      if (!report || !report.id || this.isContactReportResolving(report)) return false;
      const approve = action === 'aprobar_restituir';
      if (approve && report.estado === 'aprobado') return false;
      if (!approve && report.estado === 'rechazado') return false;

      const key = this.contactReportResolveKey(report);
      this.$set(this.contactReportsResolving, key, true);
      try {
        await axios.post(`${window.hostname}api/admin/jobbing_points/contact_reports/${report.id}/resolve`, {
          accion: action,
          notas_admin: String(notes || '').trim(),
        });
        await this.fetchContactReports({ setLoading: false });
        this.$notify({
          group: 'foos',
          title: approve ? 'Puntos restituidos' : 'Reporte actualizado',
          text: approve ? 'El reporte fue aprobado y los puntos fueron restituidos.' : 'El reporte fue rechazado.',
          type: 'success',
        });
        return true;
      } catch (error) {
        this.$notify({
          group: 'foos',
          title: 'No se pudo resolver el reporte',
          text: (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) || 'Intenta nuevamente.',
          type: 'error',
        });
        return false;
      } finally {
        this.$delete(this.contactReportsResolving, key);
      }
    },

    openRefundCreateModal() {
      this.newRefund = {
        pago_id: '',
        monto: '',
        motivo: '',
        notas_internas: '',
        send_email: true,
      };
      this.showRefundCreateModal = true;
    },

    closeRefundCreateModal() {
      if (this.refundCreateSubmitting) return;
      this.showRefundCreateModal = false;
    },

    async submitRefundCreate() {
      if (this.refundCreateSubmitting) return;
      const pagoId = Number(this.newRefund.pago_id);
      const monto = Number(this.newRefund.monto);
      if (!pagoId || !monto || !String(this.newRefund.motivo || '').trim()) {
        window.alert('Debes completar pago, monto y motivo.');
        return;
      }

      this.refundCreateSubmitting = true;
      try {
        const response = await axios.post(`${window.hostname}/api/superadmin/refunds`, {
          motivo: this.newRefund.motivo,
          notas_internas: this.newRefund.notas_internas,
          send_email: this.newRefund.send_email,
          pagos: [{ pago_id: pagoId, monto_reembolso_cliente: monto }],
        });
        const emailError = response && response.data ? response.data.email_error : null;
        this.showRefundCreateModal = false;
        await this.fetchDashboardData();
        if (emailError) {
          window.alert(emailError);
        }
      } catch (error) {
        window.alert((error && error.response && error.response.data && error.response.data.message) || 'No se pudo crear el reembolso.');
      } finally {
        this.refundCreateSubmitting = false;
      }
    },

    openRefundDetailModal(refund) {
      this.selectedRefund = refund;
      this.showRefundDetailModal = true;
    },

    closeRefundDetailModal() {
      this.showRefundDetailModal = false;
      this.selectedRefund = null;
    },

    openRefundSettleModal(refund) {
      this.selectedRefund = refund;
      this.refundSettleForm = {
        pago_notas: '',
        file: null,
      };
      this.showRefundSettleModal = true;
    },

    closeRefundSettleModal() {
      if (this.refundSettleSubmitting) return;
      this.showRefundSettleModal = false;
      this.refundSettleForm = {
        pago_notas: '',
        file: null,
      };
    },

    onRefundSettleFileChange(event) {
      const fileList = event && event.target ? event.target.files : null;
      this.refundSettleForm.file = fileList && fileList.length ? fileList[0] : null;
    },

    async submitRefundSettle() {
      if (!this.selectedRefund || !this.selectedRefund.id || this.refundSettleSubmitting) return;
      if (!this.refundSettleForm.file) {
        window.alert('Debes adjuntar el comprobante del reembolso.');
        return;
      }

      this.refundSettleSubmitting = true;
      try {
        const jsonBlob = new Blob([JSON.stringify({
          pago_notas: this.refundSettleForm.pago_notas,
        })], { type: 'application/json' });
        const data = new FormData();
        data.append('document', jsonBlob);
        data.append('file', this.refundSettleForm.file);
        await axios.post(`${window.hostname}/api/superadmin/refunds/${this.selectedRefund.id}/settle`, data);
        this.showRefundSettleModal = false;
        await this.fetchDashboardData();
      } catch (error) {
        window.alert((error && error.response && error.response.data && error.response.data.message) || 'No se pudo marcar el reembolso como pagado.');
      } finally {
        this.refundSettleSubmitting = false;
      }
    },

    async resendRefundEmail(refund) {
      if (!refund || !refund.id) return;
      try {
        await axios.post(`${window.hostname}/api/superadmin/refunds/${refund.id}/send_email`);
        await this.fetchDashboardData();
      } catch (error) {
        window.alert((error && error.response && error.response.data && error.response.data.message) || 'No se pudo reenviar el correo.');
      }
    },

    async cancelRefund(refund) {
      if (!refund || !refund.id) return;
      if (!window.confirm(`¿Cancelar reembolso #${refund.id}?`)) return;
      try {
        await axios.post(`${window.hostname}/api/superadmin/refunds/${refund.id}/cancel`);
        await this.fetchDashboardData();
      } catch (error) {
        window.alert((error && error.response && error.response.data && error.response.data.message) || 'No se pudo cancelar el reembolso.');
      }
    },

    async copyRefundLink(refund) {
      if (!refund || !refund.public_link) return;
      try {
        await navigator.clipboard.writeText(refund.public_link);
      } catch (error) {
        window.prompt('Copia el link del reembolso:', refund.public_link);
      }
    },

    formatRefundState(refund) {
      return (refund && (refund.estado_label || refund.estado)) || '-';
    },

    formatRefundNotification(refund) {
      return refund && refund.notificado ? 'Sí' : 'No';
    },

    changeJobbingB2BSort(key) {
      if (!key) return;
      if (this.jobbingB2BSortKey === key) {
        this.jobbingB2BSortOrder = this.jobbingB2BSortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.jobbingB2BSortKey = key;
        this.jobbingB2BSortOrder = 'desc';
      }
      this.sortJobbingB2BRequests();
    },

    applyJobbingFromDateFilter() {
      this.sectionIsLoading.jobbingB2B = true;
      this.fetchDashboardData();
    },

    addJobbingEmpresaFilter() {
      const selection = this.jobbingB2BEmpresaSelection;
      if (!selection) return;
      const alreadySelected = this.jobbingB2BEmpresaFilters.includes(Number(selection));
      if (!alreadySelected) {
        this.jobbingB2BEmpresaFilters = [
          ...this.jobbingB2BEmpresaFilters,
          Number(selection),
        ];
      }
      this.jobbingB2BEmpresaSelection = '';
    },

    removeJobbingEmpresaFilter(id) {
      this.jobbingB2BEmpresaFilters = this.jobbingB2BEmpresaFilters.filter((empId) => empId !== id);
    },

    addJobbingEstadoFilter() {
      const selection = this.jobbingB2BEstadoSelection;
      if (!selection) return;
      if (!this.jobbingB2BEstadoFilters.includes(selection)) {
        this.jobbingB2BEstadoFilters = [...this.jobbingB2BEstadoFilters, selection];
      }
      this.jobbingB2BEstadoSelection = '';
    },

    removeJobbingEstadoFilter(value) {
      this.jobbingB2BEstadoFilters = this.jobbingB2BEstadoFilters.filter((estado) => estado !== value);
    },

    getSortedJobbingB2BRequests(list = []) {
      const direction = this.jobbingB2BSortOrder === 'asc' ? 1 : -1;
      return [...(list || [])].sort((a, b) => {
        const aVal = this.getJobbingB2BSortValue(a, this.jobbingB2BSortKey);
        const bVal = this.getJobbingB2BSortValue(b, this.jobbingB2BSortKey);

        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        return aVal > bVal ? direction : -direction;
      });
    },

    sortJobbingB2BRequests() {
      this.jobbingB2BRequests = this.getSortedJobbingB2BRequests(this.jobbingB2BRequests);
    },

    getJobbingB2BSortValue(sol, key) {
      if (!sol) return null;

      if (key === 'empresa') {
        return (sol.empresa_nombre || '').toLowerCase();
      }

      if (key === 'fecha_creado') {
        return sol.fecha_creado ? new Date(sol.fecha_creado).getTime() : null;
      }

      if (key === 'orden_fecha') {
        return sol.orden && sol.orden.fecha_creado ? new Date(sol.orden.fecha_creado).getTime() : null;
      }

      if (key === 'presupuesto_fecha') {
        return sol.presupuesto && sol.presupuesto.fecha_creado ? new Date(sol.presupuesto.fecha_creado).getTime() : null;
      }

      if (key === 'profesional') {
        return (sol.orden && sol.orden.profesional_nombre ? sol.orden.profesional_nombre : '').toLowerCase();
      }

      if (key === 'id') {
        return sol.id ?? null;
      }

      if (key === 'estado') {
        return (sol.estado && sol.estado.label ? sol.estado.label : '').toLowerCase();
      }

      if (key === 'factura') {
        const count = sol && sol.orden ? Number(sol.orden.facturas_count || 0) : 0;
        return count > 0 ? 1 : 0;
      }

      return null;
    },

    getPagoEstadoClass(estado) {
      switch (estado) {
        case 1: // ESTADO_PAGO_PAGADO
          return 'pago-estado-pagado';
        case 2: // ESTADO_PAGO_REVERSADO
          return 'pago-estado-reversado';
        case 3: // ESTADO_PAGO_EN_REVISION
          return 'pago-estado-revision';
        case 0: // ESTADO_PAGO_PENDIENTE
        default:
          return 'pago-estado-pendiente';
      }
    },

    getEstadoValue(sol) {
      if (!sol || !sol.estado || !sol.estado.label) return 'sin_estado';
      return sol.estado.label.toLowerCase();
    },

    getEstadoLabel(value) {
      if (!value || value === 'sin_estado') return 'Sin estado';
      const match = (this.jobbingB2BRequests || []).find(
        (sol) => this.getEstadoValue(sol) === value && sol.estado && sol.estado.label,
      );
      return match && match.estado ? match.estado.label : value;
    },

    getEmpresaNombre(id) {
      if (!id) return 'Empresa desconocida';
      const empresa = this.empresasAdministracion.find((e) => e.id === id);
      return empresa ? empresa.nombre : `Empresa #${id}`;
    },

    getFilteredJobbingB2BRequests(list = []) {
      return (list || []).filter((sol) => {
        const hasWarning = Boolean(sol && (
          sol.alerta_sin_presupuesto ||
          sol.alerta_realizado_sin_factura ||
          sol.alerta_aprobado_no_realizado
        ));

        if (this.jobbingB2BEmpresaFilters.length) {
          if (!sol || !this.jobbingB2BEmpresaFilters.includes(Number(sol.empresa_id))) {
            return false;
          }
        }

        if (this.jobbingB2BEstadoFilters.length) {
          const estadoValue = this.getEstadoValue(sol);
          const matchEstado = estadoValue
            ? this.jobbingB2BEstadoFilters.includes(estadoValue)
            : this.jobbingB2BEstadoFilters.includes('sin_estado');
          if (!matchEstado) {
            return false;
          }
        }

        if (this.jobbingB2BWarningFilter === 'with') {
          return hasWarning;
        }

        if (this.jobbingB2BWarningFilter === 'without') {
          return !hasWarning;
        }

        return true;
      });
    },

    facturaTooltip(sol) {
      const count = sol && sol.orden ? Number(sol.orden.facturas_count || 0) : 0;
      const label = count === 1 ? 'factura' : 'facturas';
      return `${count} ${label}`;
    },

    getPresupuestoCount(sol) {
      if (!sol || !sol.presupuesto) return 0;
      if (typeof sol.presupuesto.count !== 'undefined') {
        return Number(sol.presupuesto.count) || 0;
      }
      if (Array.isArray(sol.presupuesto.presupuestos)) {
        return sol.presupuesto.presupuestos.length;
      }
      return sol.presupuesto.id ? 1 : 0;
    },

    presupuestoTooltip(sol) {
      const presupuestos = sol?.presupuesto?.presupuestos || [];
      if (!presupuestos.length) {
        return '0 presupuestos';
      }

      const lines = presupuestos.map((p) => {
        const dias = p.dias_desde_creado !== null && p.dias_desde_creado !== undefined
          ? this.formatDaysAgo(p.dias_desde_creado)
          : '—';
        const fecha = p.fecha_creado ? this.formatDisplayDate(p.fecha_creado, false) : 'Fecha desconocida';
        return `Presupuesto #${p.id || '?'} - ${dias} (${fecha})`;
      });

      return lines.join('\n');
    },

    toggleJobbingB2BRow(id) {
      if (!id) return;
      const expanded = new Set(this.jobbingB2BExpanded);
      if (expanded.has(id)) {
        expanded.delete(id);
      } else {
        expanded.add(id);
      }
      this.jobbingB2BExpanded = Array.from(expanded);
    },

    isJobbingB2BExpanded(id) {
      return this.jobbingB2BExpanded.includes(id);
    },

      getWarmSortValue(conv, key) {
        if (!conv) return null;

      if (key === 'interest') {
        return conv.response_json?.interest_score ?? -Infinity;
      }

      if (key === 'client_requests_extra_info') {
        return conv.client_requests_extra_info ? 1 : 0;
      }

      if (key === 'flagged_at' || key === 'solicitud_fecha' || key === 'updated_at') {
        if (!conv[key]) return null;
        const time = new Date(conv[key]).getTime();
        return Number.isNaN(time) ? null : time;
      }

      if (key === 'solicitud_clase') {
        return this.mapSolicitudClase(conv.solicitud_clase).order;
      }

      const value = conv[key];
      if (typeof value === 'string') return value.toLowerCase();
      return value;
    },

    sortWarmConversations(conversations) {
      const sorted = [...conversations];
      const direction = this.warmSortOrder === 'asc' ? 1 : -1;
      const key = this.warmSortKey;

      sorted.sort((a, b) => {
        const valA = this.getWarmSortValue(a, key);
        const valB = this.getWarmSortValue(b, key);

        if (valA === valB) return 0;
        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;

        if (valA > valB) return direction;
        if (valA < valB) return -direction;
        return 0;
      });

      return sorted;
    },

    isVisitaUpdating(visitaId) {
      return this.visitasUpdating.includes(visitaId);
    },

    isVisitaRemoving(visitaId) {
      return this.visitasRemoving.includes(visitaId);
    },

    startVisitaEdit(visita) {
      if (!visita || !visita.id) return;
      this.editingVisitaId = visita.id;
      this.visitaEditDraft = {
        fecha: visita.fecha || '',
        hora: this.normalizeVisitaHour(visita.hora),
      };
    },

    cancelVisitaEdit() {
      this.editingVisitaId = null;
      this.visitaEditDraft = { fecha: '', hora: '' };
    },

    normalizeVisitaHour(value) {
      if (!value || value === 'N/A') return '';
      const hour = String(value).trim();
      if (/^\d{2}:\d{2}$/.test(hour)) return hour;
      const parts = hour.split(':');
      if (parts.length < 2) return '';
      const hh = String(parts[0]).padStart(2, '0');
      const mm = String(parts[1]).padStart(2, '0');
      return `${hh}:${mm}`;
    },

    parseHourToMinutes(value) {
      if (!value) return null;
      const raw = String(value).trim();
      const match = raw.match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return null;
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
      return (hours * 60) + minutes;
    },

    async saveVisitaEdit(visita) {
      if (!visita || !visita.id || this.editingVisitaId !== visita.id) return;
      if (this.isVisitaUpdating(visita.id) || this.isVisitaRemoving(visita.id)) return;

      const fecha = this.visitaEditDraft.fecha;
      const desde = this.parseHourToMinutes(this.visitaEditDraft.hora);
      if (!fecha || desde === null) {
        window.alert('Debes ingresar una fecha y hora válidas.');
        return;
      }

      let hasta = Number(visita.hasta);
      if (!Number.isFinite(hasta) || hasta <= desde || hasta > 1440) {
        const baseDuration = Number(visita.hasta) - Number(visita.desde);
        const duration = Number.isFinite(baseDuration) && baseDuration > 0 ? baseDuration : 60;
        hasta = Math.min(1440, desde + duration);
        if (hasta <= desde) {
          window.alert('No se pudo calcular un rango horario válido para la visita.');
          return;
        }
      }

      this.visitasUpdating.push(visita.id);
      try {
        await axios.patch(`${window.hostname}/api/superadmin/visitas/${visita.id}`, {
          fecha,
          desde,
          hasta,
        });
        this.cancelVisitaEdit();
        await this.fetchDashboardData();
      } catch (error) {
        console.error('Error actualizando visita', error);
        const message = error.response?.data?.error || 'No se pudo actualizar la visita.';
        window.alert(message);
      } finally {
        this.visitasUpdating = this.visitasUpdating.filter((id) => id !== visita.id);
      }
    },

    async removeVisitaWithConfirmation(visita) {
      if (!visita || !visita.id) return;
      if (this.isVisitaUpdating(visita.id) || this.isVisitaRemoving(visita.id)) return;
      this.visitaToDelete = visita;
      this.showVisitaDeleteModal = true;
    },

    closeVisitaDeleteModal(force = false) {
      if (!force && this.visitaToDelete && this.isVisitaRemoving(this.visitaToDelete.id)) return;
      this.showVisitaDeleteModal = false;
      this.visitaToDelete = null;
    },

    async confirmVisitaDelete() {
      const visita = this.visitaToDelete;
      if (!visita || !visita.id) return;
      if (this.isVisitaUpdating(visita.id) || this.isVisitaRemoving(visita.id)) return;

      this.visitasRemoving.push(visita.id);
      try {
        await axios.delete(`${window.hostname}/api/superadmin/visitas/${visita.id}`);
        this.proximasVisitas = this.proximasVisitas.filter((row) => row.id !== visita.id);
        if (this.editingVisitaId === visita.id) {
          this.cancelVisitaEdit();
        }
        this.closeVisitaDeleteModal(true);
      } catch (error) {
        console.error('Error eliminando visita', error);
        const message = error.response?.data?.error || 'No se pudo eliminar la visita.';
        window.alert(message);
      } finally {
        this.visitasRemoving = this.visitasRemoving.filter((id) => id !== visita.id);
      }
    },

    isPresupuestoRemovalInProgress(ordenId) {
      return this.presupuestosPendientesRemoving.includes(ordenId);
    },

    async markPresupuestoNoRequerido(ordenId) {
      if (this.isPresupuestoRemovalInProgress(ordenId)) {
        return;
      }

      const confirmed = window.confirm('¿Seguro que deseas marcar la orden como sin presupuesto requerido?');
      if (!confirmed) {
        return;
      }

      this.presupuestosPendientesRemoving.push(ordenId);
      try {
        await axios.post(`${window.hostname}/api/superadmin/ordenes/${ordenId}/presupuesto_no_requerido`);
        this.presupuestosPendientesEnviar = this.presupuestosPendientesEnviar.filter(p => p.orden_id !== ordenId);
      } catch (error) {
        console.error('Error marcando presupuesto como no requerido', error);
        const message = error.response?.data?.error || 'No se pudo marcar el presupuesto como no requerido.';
        window.alert(message);
      } finally {
        const index = this.presupuestosPendientesRemoving.indexOf(ordenId);
        if (index !== -1) {
          this.presupuestosPendientesRemoving.splice(index, 1);
        }
      }
    },

    async markWarmConversationResolved(scanId) {
      try {
        await axios.post(`${window.hostname}/api/superadmin/warm_conversations/resolve`, { scan_id: scanId });
        const conv = this.warmConversations.find(c => c.scan_id === scanId);
        if (conv) conv.resolved = true;
      } catch (err) {
        console.error('Error resolving warm conversation', err);
      }
    },
    async copyWarmClientList() {
      const filtered = this.filteredWarmConversations;
      if (this.warmProfessionalFilters.length !== 1) {
        window.alert('Selecciona exactamente un profesional para copiar la lista.');
        return;
      }
      const includeInterest = window.confirm('¿Quieres incluir el interés del cliente en la lista?');
      const professionalNames = new Set(
        filtered.map((conv) => conv.profesional_nombre).filter((name) => name)
      );
      const professionalName =
        professionalNames.size === 1
          ? Array.from(professionalNames)[0].split(' ')[0]
          : 'equipo';
      const entries = filtered.map((conv) => {
        const interestScore =
          conv && conv.response_json && typeof conv.response_json === 'object'
            ? conv.response_json.interest_score
            : null;
        const interestLabel = this.mapInterestScore(interestScore).text;
        return includeInterest
          ? `- ${conv.cliente_nombre} (${interestLabel})`
          : `- ${conv.cliente_nombre}`;
      });
      const intro = `Hola ${professionalName}, me podrías ayudar revisando estos chats?\n`;
      const payload = entries.length ? `${intro}${entries.join('\n')}\n` : '';

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(payload);
          return;
        }
      } catch (error) {
        console.warn('No se pudo copiar con Clipboard API, intentando fallback.', error);
      }

      const textarea = document.createElement('textarea');
      textarea.value = payload;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    },

    addWarmProfessional(prof) {
      if (!prof) return;
      const exists = this.warmProfessionalFilters.some(p => p.id === prof.id);
      if (!exists) {
        this.warmProfessionalFilters.push(prof);
      }
      this.warmProfessionalQuery = '';
      this.warmProfessionalResults = [];
    },

    removeWarmProfessional(id) {
      this.warmProfessionalFilters = this.warmProfessionalFilters.filter((p) => p.id !== id);
    },

    toggleWarmInterest(value) {
      if (!value) return;
      if (this.warmInterestFilters.includes(value)) {
        this.warmInterestFilters = this.warmInterestFilters.filter((v) => v !== value);
      } else {
        this.warmInterestFilters = [...this.warmInterestFilters, value];
      }
    },

    resetWarmPagination() {
      this.sectionPages.warmConversations = 1;
    },

    getDefaultWarmStartDate() {
      const now = new Date();
      now.setDate(now.getDate() - 7);
      return now.toISOString().slice(0, 10);
    },

    getDefaultJobbingB2BFromDate() {
      const now = new Date();
      now.setMonth(now.getMonth() - 2);
      return now.toISOString().slice(0, 10);
    },

    async fetchWarmConversation(scanId, { force = false } = {}) {
      if (!scanId) return;
      if (!force && this.warmConversationMessages[scanId]) return;
      this.warmConversationLoading = true;
      this.warmConversationError = null;
      try {
        const resp = await axios.get(`${window.hostname}/api/superadmin/warm_conversations/${scanId}/conversation`);
        const messages = resp?.data?.messages || [];
        this.$set(this.warmConversationMessages, scanId, messages);
      } catch (err) {
        console.error('Error loading warm conversation', err);
        this.warmConversationError = 'No se pudo cargar la conversación.';
      } finally {
        this.warmConversationLoading = false;
      }
    },

    toggleWarmConversation(conv) {
      if (!conv) return;
      if (this.expandedWarmConversationId === conv.scan_id) {
        this.expandedWarmConversationId = null;
        this.warmConversationError = null;
        return;
      }
      this.expandedWarmConversationId = conv.scan_id;
      this.warmConversationError = null;
      this.fetchWarmConversation(conv.scan_id, { force: true });
    },

    // --- Existing Helper Methods (Condensed for brevity - keep full logic) ---

    isLastMessageFromClient(conv) {
      if (!conv) return false;
      const value = conv.last_message_from_client;
      if (value === true || value === false) return value;
      if (typeof value === 'number') return value === 1;
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'si'].includes(normalized)) return true;
        if (['false', '0', 'no', ''].includes(normalized)) return false;
      }
      return false;
    },
    mapInterestScore(score) {
      if (score === null || score === undefined) {
        return { text: '-', className: '' };
      }
      if (score < 25) {
        return { text: 'Sin interés', className: 'interest-none' };
      }
      if (score < 50) {
        return { text: 'Bajo', className: 'interest-low' };
      }
      if (score < 75) {
        return { text: 'Medio', className: 'interest-medium' };
      }
      if (score < 100) {
        return { text: 'Alto', className: 'interest-high' };
      }
      return { text: 'Confirmado', className: 'interest-confirmed' };
    },
    mapVisitaStatus(s) { /* ... mapping logic ... */ switch(s){case 0:return{text:'Pendiente',className:'status-pending'};case 1:return{text:'Agendada',className:'status-scheduled'};case 2:return{text:'Confirmada',className:'status-accepted'};case 3:return{text:'En proceso',className:'status-inprogress'};case 4:return{text:'Completada',className:'status-completed'};default:return{text:`Desc(${s})`,className:'status-default'};} },
    normalizeSolicitudTipoValue(tipo) {
      if (tipo == null) {
        return '';
      }

      return tipo
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
    },
    mapSolicitudType(tipo) {
      const normalized = this.normalizeSolicitudTipoValue(tipo);

      if (normalized === 'privada' || normalized === 'privado') {
        return { text: 'Privada', className: 'type-privada' };
      }

      if (normalized === 'publica' || normalized === 'publico') {
        return { text: 'Pública', className: 'type-publica' };
      }

      return { text: 'Sin tipo', className: 'type-desconocido' };
    },
    mapSolicitudClase(clase) {
      const normalized = (clase || '').toString().trim().toLowerCase();

      switch (normalized) {
        case 'grande':
          return { text: 'grande', order: 3, className: 'clase-grande' };
        case 'mediana':
          return { text: 'mediana', order: 2, className: 'clase-mediana' };
        case 'chica':
          return { text: 'chica', order: 1, className: 'clase-chica' };
        case 'desconocido':
          return { text: 'desconocido', order: 0, className: 'clase-desconocido' };
        default:
          return { text: 'sin identificar', order: -1, className: 'clase-sin-identificar' };
      }
    },
    getStatusClass(s) { /* ... mapping logic ... */ const l=s?s.toLowerCase():'';switch(l){case 'en progreso':case 'en proceso':return 'status-inprogress';case 'agendado':return 'status-scheduled';case 'completado':case 'atendido':return 'status-completed';case 'confirmado':return 'status-accepted';case 'esperando materiales':return 'status-waiting';case 'cancelado':return 'status-cancelled';case 'pendiente':return 'status-pending';default:return 'status-default';} },
    formatDisplayDate(d, t = true) { /* ... formatting logic ... */ if(!d)return 'N/A';try{const m=d.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);if(!m)return d;const[,y,mo,da,h='00',mi='00']=m;if(!t){return `${da}/${mo}/${y}`;}return `${da}/${mo}/${y} ${h}:${mi}`;}catch(e){console.warn("Date format error:",d,e);return d;} },
    formatCurrency(value) {
      if (value === null || value === undefined || isNaN(Number(value))) {
        return "N/A";
      }
      // Ensure value is a number for toLocaleString
      const numericValue = Number(value);
      return `$${numericValue.toLocaleString('es-CL')}`;
    },
    formatDaysAgo(days) {
      if (days === null || days === undefined) return '-';
      return `${days} día${days === 1 ? '' : 's'}`;
    },
    overdueItemTooltip(item) {
      if (!item || item.dias_desde_aprobacion === null || item.dias_desde_aprobacion === undefined) {
        return 'Ítem aprobado sin realizar';
      }
      return `Aprobado hace ${this.formatDaysAgo(item.dias_desde_aprobacion)}`;
    },
    getComunaNombre(comunaId) {
      if (!comunaId || !Array.isArray(this.comunas)) return 'Sin comuna';
      const comuna = this.comunas.find((c) => c.id === comunaId);
      return comuna ? comuna.Nombre : 'Sin comuna';
    },
  },
  computed: {
    dashboardRefreshButtonDisabled() {
      return this.isInitialLoading
        || this.dashboardIsRefreshing
        || Object.values(this.sectionIsLoading).some(Boolean);
    },

    dashboardUpdatedAgoText() {
      if (!this.lastDashboardRefreshAt) return 'Aun no actualizado';
      const elapsedMs = Math.max(0, this.dashboardNow - this.lastDashboardRefreshAt);
      const elapsedSeconds = Math.floor(elapsedMs / 1000);
      if (elapsedSeconds < 10) return 'Actualizado recien';
      if (elapsedSeconds < 60) return 'Actualizado hace menos de 1 min';

      const elapsedMinutes = Math.floor(elapsedSeconds / 60);
      if (elapsedMinutes < 60) {
        return `Actualizado hace ${elapsedMinutes} min`;
      }

      const elapsedHours = Math.floor(elapsedMinutes / 60);
      if (elapsedHours < 24) {
        return `Actualizado hace ${elapsedHours} h`;
      }

      const elapsedDays = Math.floor(elapsedHours / 24);
      return `Actualizado hace ${elapsedDays} d`;
    },

    contactReportResolutionIsApprove() {
      return this.contactReportResolutionAction === 'aprobar_restituir';
    },

    contactReportResolutionSubmitting() {
      return this.contactReportResolutionTarget
        ? this.isContactReportResolving(this.contactReportResolutionTarget)
        : false;
    },

    contactReportResolutionTitle() {
      return this.contactReportResolutionIsApprove
        ? 'Aprobar reporte y restituir puntos'
        : 'Rechazar reporte';
    },

    contactReportResolutionSubmitLabel() {
      if (this.contactReportResolutionSubmitting) {
        return this.contactReportResolutionIsApprove ? 'Restituyendo...' : 'Rechazando...';
      }
      return this.contactReportResolutionIsApprove ? 'Aprobar y restituir' : 'Rechazar reporte';
    },

    contactReportResolutionToneClass() {
      return this.contactReportResolutionIsApprove ? 'approve' : 'reject';
    },

    contactReportResolutionTargetTitle() {
      const report = this.contactReportResolutionTarget;
      if (!report) return '-';
      if (report.solicitud && report.solicitud.titulo) {
        return `#${report.solicitud.id || report.solicitud_cliente_id} - ${report.solicitud.titulo}`;
      }
      return `Solicitud #${report.solicitud_cliente_id || report.id}`;
    },

    contactReportResolutionProfessionalName() {
      const report = this.contactReportResolutionTarget;
      if (!report) return '-';
      return report.profesional && report.profesional.nombre
        ? report.profesional.nombre
        : `Profesional #${report.profesional_id || '-'}`;
    },

    contactReportResolutionPointsLabel() {
      const report = this.contactReportResolutionTarget;
      const points = Number((report && report.puntos_jobbing_gastados) || 0);
      return `${points.toLocaleString('es-CL')} pts`;
    },

    jobbingB2BEstadoOptions() {
      const labelMap = new Map();
      labelMap.set('sin_estado', 'Sin estado');
      (this.jobbingB2BRequests || []).forEach((sol) => {
        const value = this.getEstadoValue(sol);
        const label = sol && sol.estado && sol.estado.label ? sol.estado.label : null;
        if (value) {
          labelMap.set(value, label || this.getEstadoLabel(value));
        }
      });
      return Array.from(labelMap.entries()).map(([value, label]) => ({ value, label }));
    },
    sortedJobbingB2BRequests() {
      const filtered = this.getFilteredJobbingB2BRequests(this.jobbingB2BRequests);
      return this.getSortedJobbingB2BRequests(filtered);
    },
    filteredSolicitudesSinRespuesta() {
      if (this.solicitudesTipoFiltro === 'Todas') {
        return this.solicitudesSinRespuesta;
      }
      const filtroNormalizado = this.normalizeSolicitudTipoValue(this.solicitudesTipoFiltro);
      return this.solicitudesSinRespuesta.filter((solicitud) => {
        const tipoNormalizado = this.normalizeSolicitudTipoValue(solicitud.tipo);
        return tipoNormalizado === filtroNormalizado;
      });
    },
    filteredWarmConversations() {
      const startDate = this.warmStartDate ? new Date(this.warmStartDate) : null;
      if (startDate) {
        startDate.setHours(0, 0, 0, 0);
      }

      return this.warmConversations.filter((conv) => {
        const solicitudEstado =
          conv && conv.solicitud_estado !== undefined ? conv.solicitud_estado : null;
        const solicitudActive = solicitudEstado === null || Number(solicitudEstado) !== 0;
        const professionalMatch =
          !this.warmProfessionalFilters.length ||
          this.warmProfessionalFilters.some((p) => p.id === conv.profesional_id);

        const interestScore =
          conv && conv.response_json && typeof conv.response_json === 'object'
            ? conv.response_json.interest_score
            : null;
        const interestLabel = this.mapInterestScore(interestScore).text;
        const interestMatch =
          !this.warmInterestFilters.length || this.warmInterestFilters.includes(interestLabel);

        const lastMessageMatch = !this.onlyClientLastMessage || this.isLastMessageFromClient(conv);
        const conversationDate = conv.flagged_at || conv.solicitud_fecha;
        const dateMatch =
          !startDate || (conversationDate && new Date(conversationDate).setHours(0, 0, 0, 0) >= startDate.getTime());

        return solicitudActive && professionalMatch && interestMatch && lastMessageMatch && dateMatch;
      });
    },
    sortedWarmConversations() {
      return this.sortWarmConversations(this.filteredWarmConversations);
    },
    paginatedWarmConversations() {
      const start = (this.sectionPages.warmConversations - 1) * this.limitPerPage;
      return this.sortedWarmConversations.slice(start, start + this.limitPerPage);
    },
    warmIsLastPage() {
      const total = this.filteredWarmConversations.length;
      const totalPages = Math.max(1, Math.ceil(total / this.limitPerPage));
      return this.sectionPages.warmConversations >= totalPages;
    },
    currentWarmMessages() {
      if (!this.expandedWarmConversationId) return [];
      return this.warmConversationMessages[this.expandedWarmConversationId] || [];
    },
    warmInterestOptions() {
      return [
        { value: 'Sin interés', label: 'Sin interés' },
        { value: 'Bajo', label: 'Bajo' },
        { value: 'Medio', label: 'Medio' },
        { value: 'Alto', label: 'Alto' },
        { value: 'Confirmado', label: 'Confirmado' },
      ];
    },
    ...mapGetters(['comunas', 'categorias', 'regiones', 'tipos', 'admin_datos', 'token'])
  },
  mounted() {
    this.startDashboardRelativeTimer();
    // Use nextTick for the redirect check in case admin_datos isn't immediately available
    this.$nextTick(() => {
     if (!this.admin_datos || !this.admin_datos.superadmin) {
        console.warn("User is not a superadmin or admin_datos not loaded initially (mounted), redirecting.");
        this.$router.push('/'); // Redirect to home or login page
      }
    });
  },
  activated() {
    this.startDashboardRelativeTimer();
    if (this.admin_datos && this.admin_datos.superadmin) {
      if (this.shouldRefreshDashboardAfterInactive()) {
        this.fetchDashboardData();
      }
    }
    this.dashboardDeactivatedAt = null;
  },
  deactivated() {
    this.dashboardDeactivatedAt = Date.now();
    this.stopDashboardRelativeTimer();
  },
  beforeDestroy() {
    this.stopDashboardRelativeTimer();
  },
  watch: {
     // Watch for changes in admin_datos if it loads asynchronously
     admin_datos(newVal, oldVal) {
       if (newVal) { // Check only when newVal is available
           if (!newVal.superadmin) {
              console.warn("Admin_datos updated, user is not superadmin, redirecting from watcher.");
              this.$nextTick(() => { this.$router.push('/'); });
           } else if (!oldVal && newVal.superadmin) {
              // If admin_datos just became available and user is superadmin, fetch initial data
              console.log("Admin_datos loaded and user is superadmin, fetching initial data.");
              this.fetchDashboardData();
              this.loadProfesionales();
              this.loadEmpresasAdministracion();
           }
       } else if (oldVal && oldVal.superadmin) {
           // Handle case where user logs out or loses superadmin status
           console.warn("Admin_datos became null/undefined or user lost superadmin, redirecting.");
           this.$nextTick(() => { this.$router.push('/'); });
       }
     },

     profesionalPendienteQuery(val) {
       if (this.fProfesionales) {
         this.resultadoProfesionalesPendientes = val ? this.fProfesionales.search(val) : [];
       }
     },
     warmProfessionalQuery(val) {
       if (this.fProfesionales) {
         this.warmProfessionalResults = val ? this.fProfesionales.search(val) : [];
       }
     },
     warmProfessionalFilters() {
       this.resetWarmPagination();
     },
    warmInterestFilters() {
      this.resetWarmPagination();
    },
    onlyClientLastMessage() {
      this.resetWarmPagination();
    },
   warmStartDate() {
     this.resetWarmPagination();
     this.fetchDashboardData();
    },

  }
}
</script>

<style scoped>
.compras-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-top: 12px; }
.compras-filters > input { flex: 1; min-width: 240px; }
.compras-filters label { display: flex; align-items: center; gap: 6px; }
.compras-filters input { padding: 6px 8px; border: 1px solid #ccc; border-radius: 4px; }
/* --- Base & Existing Styles (Unchanged) --- */
.warning-icon { display: inline-block; margin-left: 8px; color: #ffc107; font-weight: bold; cursor: default; }
td, th { text-align: left; padding: 8px 12px; border-bottom: 1px solid #eee; vertical-align: middle; }
th { background-color: #f8f9fa; font-weight: 600; vertical-align: middle; }
.sortable { cursor: pointer; }
tr:hover { background-color: #f1f1f1; }
table { margin-left: 0; margin-right: 0; width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 0.9em; }
.column-right { background-color: rgb(242, 247, 251); padding-top: 80px; padding-left: 260px; width: 100%; min-height: 100vh; box-sizing: border-box; }
.main-container { background-color: white; margin: 20px; padding: 20px 20px 10px 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); }
.dashboard-layout-actions { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
.dashboard-refresh-status { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.dashboard-updated-at { color: #666; font-size: 0.9em; }
.dashboard-grid { display: grid; grid-template-columns: 1fr; gap: 25px; }
@media (min-width: 1200px) { .dashboard-grid { grid-template-columns: 1fr 1fr; } }
.dashboard-grid.single-column { grid-template-columns: 1fr !important; }
.dashboard-section { background-color: #fff; padding: 0px 20px 15px 20px; border-radius: 6px; border: 1px solid #e9ecef; overflow: hidden; }
.dashboard-section h3 { margin-top: 0; margin-bottom: 0px; color: #333; font-size: 1.1em; }
.table-container { max-width: 100%; overflow-x: auto; margin-top: 5px; min-height: 100px; /* Give space for loader */ position: relative; /* Needed for potential overlay loader */ }
.badge { display: inline-block; padding: 4px 10px; border-radius: 12px; font-size: 0.8em; font-weight: 600; line-height: 1; text-align: center; white-space: nowrap; vertical-align: baseline; color: #fff; }
/* ... other badge styles ... */
.status-inprogress{background-color:#007bff}.status-scheduled{background-color:#ffc107;color:#212529}.status-completed{background-color:#28a745}.status-waiting{background-color:#fd7e14}.status-cancelled{background-color:#6c757d}.status-pending{background-color:#6c757d}.status-default{background-color:#17a2b8}.status-sent{background-color:#17a2b8}.status-accepted{background-color:#20c997}.status-rejected{background-color:#dc3545}.type-publica{background-color:#0dcaf0}.type-privada{background-color:#6f42c1}.type-desconocido{background-color:#6c757d}.profesional-detail{font-size:.9em;color:#555;margin-left:5px}
.status-planned{background-color:#5c7cfa}
.revision-pendiente{background-color:#6c757d}.revision-congruente{background-color:#28a745}.revision-incongruente{background-color:#dc3545}.revision-indeterminado{background-color:#ffc107;color:#212529}
.interest-none{background-color:#6c757d}
.interest-low{background-color:#ffc107;color:#212529}
.interest-medium{background-color:#17a2b8}
.interest-high{background-color:#28a745}
.interest-confirmed{background-color:#0d6efd}
.clase-grande{background-color:#0d6efd}
.clase-mediana{background-color:#17a2b8}
.clase-chica{background-color:#20c997}
.clase-desconocido{background-color:#6c757d}
.clase-sin-identificar{background-color:#adb5bd;color:#212529}
.status-warning{background-color:#ffc107;color:#212529}
.status-neutral{background-color:#e9ecef;color:#343a40}
.loading-indicator, .error-message { text-align: center; padding: 40px 20px; color: #555; }
.selected-filters { margin-top: 6px; display: flex; flex-wrap: wrap; gap: 6px; }
.filter-chip { background: #eef1f5; color: #333; border-radius: 12px; padding: 4px 8px; font-size: 0.8em; display: inline-flex; align-items: center; gap: 6px; }
.chip-remove { background: transparent; border: none; color: #555; cursor: pointer; font-weight: bold; line-height: 1; }
.chip-remove:hover { color: #000; }
.error-message { color: #dc3545; background-color: #f8d7da; border: 1px solid #f5c6cb; border-radius: 4px; margin-bottom: 20px; }
.error-message button { margin-top: 10px; padding: 5px 15px; cursor: pointer; border: 1px solid #dc3545; background-color: #dc3545; color: white; border-radius: 4px; }
.error-message button:hover { background-color: #c82333; border-color: #bd2130; }

/* --- Section Header and Pagination Styles (Unchanged) --- */
.section-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 0 10px 0; border-bottom: 1px solid #eee; margin-bottom: 10px; }
.section-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; justify-content: flex-end; }
.jobbing-b2b-header { flex-direction: column; align-items: flex-start; gap: 8px; }
.jobbing-b2b-actions { width: 100%; flex-direction: column; align-items: flex-start; gap: 8px; }
.jobbing-b2b-filter-row { width: 100%; display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.jobbing-b2b-selected-chips { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 4px; }
.payments-section-header { align-items: flex-start; }
.dashboard-tabs { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-top: 8px; }
.section-pagination-controls { display: flex; align-items: center; gap: 8px; }
.section-pagination-controls button { padding: 3px 8px; font-size: 0.9em; line-height: 1; border: 1px solid #ccc; background-color: #f8f9fa; color: #333; cursor: pointer; border-radius: 3px; transition: background-color 0.15s ease-in-out; }
.section-pagination-controls .section-tool-button { padding: 5px 10px; border-color: #7aa7df; background-color: #eef6ff; color: #174a7c; font-weight: 700; line-height: 1.1; }
.section-pagination-controls .section-tool-button:hover:not(:disabled) { background-color: #dbeeff; border-color: #4d8ed5; }
.section-pagination-controls button:hover:not(:disabled) { background-color: #e9ecef; }
.section-pagination-controls button:disabled { color: #aaa; cursor: not-allowed; background-color: #f8f9fa; }
.section-pagination-controls span { font-size: 0.9em; color: #555; min-width: 50px; text-align: center; }
.section-filter label { font-size: 0.85em; color: #555; margin-right: 6px; }
.select-filtro { padding: 4px 8px; border-radius: 4px; border: 1px solid #ccc; font-size: 0.9em; background-color: #fff; color: #333; }
.warm-filters { justify-content: space-between; align-items: flex-start; }
.warm-filter-group { display: flex; flex-direction: column; gap: 6px; min-width: 180px; }
.warm-filter-group .input-buscar-profesional { width: 240px; }
.warm-copy-group { min-width: 140px; }
.warm-copy-button { white-space: nowrap; }
.warm-conversation-panel { margin-top: 12px; border: 1px solid #e0e7f1; border-radius: 8px; background: #f9fbff; padding: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.warm-conversation-panel .panel-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; font-size: 0.9em; }
.warm-conversation-panel .panel-meta { color: #6c757d; font-weight: 600; }
.warm-conversation-panel .panel-body { max-height: 260px; overflow-y: auto; background: white; border: 1px solid #e5e9f2; border-radius: 6px; padding: 10px; }
.warm-conversation-panel .panel-body.error { color: #dc3545; }
.warm-conversation-panel .panel-body.messages { display: flex; flex-direction: column; gap: 10px; }
.warm-conversation-panel .message-row { border-bottom: 1px solid #f1f3f7; padding-bottom: 8px; }
.warm-conversation-panel .message-row:last-child { border-bottom: none; }
.warm-conversation-panel .message-meta { display: flex; justify-content: space-between; font-size: 0.85em; color: #6c757d; }
.warm-conversation-panel .sender { font-weight: 700; }
.warm-conversation-panel .sender.cliente { color: #0d6efd; }
.warm-conversation-panel .sender.profesional { color: #198754; }
.warm-conversation-panel .timestamp { font-style: italic; }
.warm-conversation-panel .message-text { margin-top: 4px; line-height: 1.4; }
.warm-conversation-panel .empty-messages { color: #6c757d; font-style: italic; text-align: center; }
.whatsapp-handoff-note { margin-top: 8px; padding: 8px 10px; border: 1px solid #ffe08a; border-radius: 6px; background: #fff8db; color: #76520b; font-size: 0.9em; font-weight: 600; }
.clickable-row { cursor: pointer; }
.clickable-row:hover { background-color: #f5f8ff; }
.dashboard-entity-link {
  border: 0;
  background: transparent;
  color: #0d6efd;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.dashboard-entity-link:hover,
.dashboard-entity-link:focus {
  color: #084298;
}
.jobbing-b2b-detail td { background-color: #f9fbff; padding: 12px; }
.jobbing-b2b-detail-header { margin-bottom: 10px; }
.jobbing-b2b-detail-header .detail-title { font-weight: 700; font-size: 1.05em; margin-bottom: 4px; }
.jobbing-b2b-detail-header .detail-description { color: #4b5563; }
.jobbing-items { display: flex; flex-direction: column; gap: 8px; }
.jobbing-items-title { font-weight: 700; color: #1f2937; }
.jobbing-items-table { width: 100%; border-collapse: collapse; }
.jobbing-items-table th, .jobbing-items-table td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
.jobbing-items-table th { background-color: #f3f4f6; color: #374151; font-weight: 700; }
.jobbing-items-table td { background-color: #fff; }
.jobbing-pagos { margin-top: 16px; display: flex; flex-direction: column; gap: 8px; }
.jobbing-pagos-table th, .jobbing-pagos-table td { text-align: center; }
.jobbing-item-row { border-bottom: 1px solid #e6ecf5; padding-bottom: 6px; }
.jobbing-item-row:last-child { border-bottom: none; padding-bottom: 0; }
.jobbing-item-header { display: flex; gap: 10px; align-items: center; }
.jobbing-item-header .item-name { font-weight: 600; }
.badge-success { background-color: #d1e7dd; color: #0f5132; }
.badge-muted { background-color: #f1f3f5; color: #555; }
.badge-warning { background-color: #fff3cd; color: #664d03; border: 1px solid #ffe69c; }
.badge-danger { background-color: #f8d7da; color: #842029; border: 1px solid #f5c2c7; }
.pago-badge { min-width: 90px; justify-content: center; }
.pago-estado-pendiente { background-color: #fff3cd; color: #664d03; border: 1px solid #ffe69c; }
.pago-estado-pagado { background-color: #d1e7dd; color: #0f5132; border: 1px solid #badbcc; }
.pago-estado-reversado { background-color: #f8d7da; color: #842029; border: 1px solid #f5c2c7; }
.pago-estado-revision { background-color: #e2e3e5; color: #41464b; border: 1px solid #d3d6d8; }
.payment-method-toggles { display: flex; flex-direction: column; gap: 6px; min-width: 150px; }
.payment-method-toggle { display: inline-flex; align-items: center; gap: 6px; color: #334155; font-size: 0.88em; font-weight: 600; white-space: nowrap; }
.payment-method-toggle input { margin: 0; }
.payment-method-toggle--locked { color: #64748b; }
.payment-method-toggle.is-saving { opacity: 0.65; }

.whatsapp-row-button {
  font-size: 12px;
  line-height: 1;
  padding: 3px 5px;
}
.warning-icon-stack { display: flex; flex-direction: column; gap: 4px; align-items: flex-start; }
.factura-badge { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 12px; font-weight: 600; }
.factura-present { background-color: #d1e7dd; color: #0f5132; border: 1px solid #badbcc; }
.factura-absent { background-color: #f1f3f5; color: #6c757d; border: 1px solid #e2e6ea; }
.presupuesto-badge { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 12px; font-weight: 600; }
.presupuesto-present { background-color: #d1e7dd; color: #0f5132; border: 1px solid #badbcc; }
.presupuesto-absent { background-color: #f1f3f5; color: #6c757d; border: 1px solid #e2e6ea; }
.overdue-badge { display: inline-flex; margin-top: 4px; }
.warm-conversation-detail-row td { background-color: #f9fbff; padding: 0; }
.filter-label { font-size: 0.85em; color: #444; font-weight: 600; }
.selected-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.filter-chip { display: inline-flex; align-items: center; gap: 6px; background-color: #e9f2ff; color: #0d6efd; padding: 4px 8px; border-radius: 12px; font-size: 0.85em; border: 1px solid #cfe2ff; }
.chip-remove { background: none; border: none; color: #0d6efd; cursor: pointer; font-size: 1em; line-height: 1; }
.interest-chips { display: flex; gap: 8px; flex-wrap: wrap; }
.chip-button { padding: 6px 10px; border-radius: 12px; border: 1px solid #ced4da; background: #fff; cursor: pointer; font-size: 0.85em; transition: all 0.15s ease-in-out; }
.chip-button.active { background-color: #0d6efd; color: #fff; border-color: #0d6efd; }
.last-message-toggle { margin-left: auto; }
.contact-count-badge {
  min-width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #dc3545;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75em;
  font-weight: 700;
}
.human-handoff-indicator {
  position: relative;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff8db;
  border: 1px solid #e0b341;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.85);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
}
.human-handoff-indicator::before,
.human-handoff-indicator::after {
  content: '';
  width: 3px;
  height: 10px;
  border-radius: 2px;
  background: #76520b;
  display: block;
}
.human-handoff-indicator::before {
  margin-right: 3px;
}
.substatus-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  color: #fff;
  font-size: 0.82em;
  font-weight: 600;
}
.ai-review-header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.ai-review-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.12);
  vertical-align: middle;
}
.detail-tab-button {
  border: 1px solid #d6deeb;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.88em;
  font-weight: 600;
  cursor: pointer;
}
.detail-tab-button.active {
  background: #e7f0ff;
  border-color: #bfd3ff;
  color: #0d6efd;
}
.add-contact-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #0d6efd;
  color: #fff;
  border: 1px solid #0d6efd;
  border-radius: 8px;
  font-size: 0.9em;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 14px;
  box-shadow: 0 1px 2px rgba(13, 110, 253, 0.25);
}
.add-contact-button:hover {
  background: #0b5ed7;
  border-color: #0a58ca;
}
.contact-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.contact-list-item { background: #fff; border: 1px solid #e7edf5; border-radius: 6px; padding: 8px; }
.contact-list-meta { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; font-size: 0.9em; }
.contact-notes { margin-top: 6px; color: #334155; }
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
}
.modal-container {
  width: min(640px, calc(100% - 24px));
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.2);
  overflow: hidden;
}
.modal-header, .modal-footer { padding: 12px 16px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #e9ecef; }
.modal-close { border: none; background: transparent; font-size: 1.3em; cursor: pointer; }
.modal-close:disabled { opacity: 0.6; cursor: not-allowed; }
.modal-content { padding: 12px 16px; display: flex; flex-direction: column; gap: 10px; }
.form-row { display: flex; flex-direction: column; gap: 6px; }
.form-row input, .form-row select, .form-row textarea {
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.95em;
}
.inline-checkbox label { display: inline-flex; gap: 8px; align-items: center; }
.modal-footer { border-top: 1px solid #e9ecef; display: flex; justify-content: flex-end; gap: 8px; }
.btn { border: 1px solid transparent; border-radius: 6px; padding: 7px 12px; cursor: pointer; }
.btn-cancel { background: #f1f3f5; color: #374151; border-color: #dee2e6; }
.btn-primary { background: #0d6efd; color: white; }
.btn:disabled { opacity: 0.65; cursor: not-allowed; }
.modal-fade-enter-active,
.modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter,
.modal-fade-leave-to { opacity: 0; }

/* --- NEW Per-Section Loader Style --- */
.section-loader {
    /* position: absolute; top: 0; left: 0; right: 0; bottom: 0; */ /* Option 1: Overlay */
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100px; /* Ensure it takes some space */
    /* background-color: rgba(255, 255, 255, 0.8); */ /* Optional semi-transparent overlay */
    color: #666;
    font-style: italic;
    padding: 20px; /* Option 2: Placeholder */
}

.section-filter { position: relative; }
.input-buscar-profesional { padding: 3px 6px; font-size: 0.9em; border: 1px solid #ccc; border-radius: 3px; }
.resultado-profesional { position: absolute; background:white; border:1px solid #ccc; width:200px; max-height:150px; overflow-y:auto; z-index:10; }
.resultado-profesional-item { padding:4px 6px; cursor:pointer; }
.resultado-profesional-item:hover { background:#f1f1f1; }
.profesional-seleccionado { margin-top:5px; font-size:0.85em; }
.clear-filter { margin-left:5px; cursor:pointer; border:none; background:transparent; }
.icon-action-button:not(.add-contact-button) {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.1em;
  line-height: 1;
  padding: 2px 6px;
}
.icon-action-button:not(.add-contact-button):disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.visita-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.visita-edit-datetime {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.visita-edit-input {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.9em;
}
.add-contact-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.action-secondary-button,
.action-danger-button {
  border-radius: 8px;
  font-size: 0.9em;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 14px;
  cursor: pointer;
}
.action-secondary-button {
  background: #f8fafc;
  color: #334155;
  border: 1px solid #cbd5e1;
}
.action-secondary-button:hover {
  background: #eef2f7;
}
.action-danger-button {
  background: #fff1f2;
  color: #b42318;
  border: 1px solid #fecdd3;
}
.action-danger-button:hover {
  background: #ffe4e6;
}
.meta-muted { color: #666; font-size: 0.85em; }
.section-subtitle {
  margin: 4px 0 0;
  color: #667085;
  font-size: 0.9em;
}
.inline-inputs {
  display: flex;
  gap: 8px;
}
.inline-inputs input {
  flex: 1 1 0;
}
.refund-actions {
  gap: 12px;
}
.refund-action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.refund-badge {
  background: #eef2ff;
  color: #3730a3;
}
.contact-reports-section {
  border-color: #fed7aa;
  box-shadow: inset 4px 0 0 #f97316;
}
.contact-reports-header {
  align-items: flex-start;
}
.contact-reports-actions {
  gap: 12px;
}
.contact-report-row-pending td {
  background: #fff7ed;
}
.contact-report-detail {
  max-width: 280px;
  white-space: normal;
}
.contact-report-contact {
  min-width: 150px;
}
.contact-report-reason {
  background: #ffedd5;
  color: #9a3412;
  border: 1px solid #fed7aa;
}
.contact-report-status-pending {
  background: #f97316;
  color: #fff;
}
.contact-report-status-approved {
  background: #dcfce7;
  color: #166534;
}
.contact-report-status-rejected {
  background: #fee2e2;
  color: #991b1b;
}
.contact-report-status-resolved {
  background: #e0f2fe;
  color: #075985;
}
.contact-report-resolution-modal {
  width: min(700px, calc(100% - 24px));
}
.contact-report-resolution-panel {
  border-radius: 10px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
}
.contact-report-resolution-panel p {
  margin: 4px 0 0;
  color: #374151;
  line-height: 1.45;
}
.contact-report-resolution-panel.approve {
  background: #ecfdf5;
  border-color: #bbf7d0;
}
.contact-report-resolution-panel.reject {
  background: #fff7ed;
  border-color: #fed7aa;
}
.contact-report-resolution-kicker {
  color: #111827;
  font-size: 0.78em;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.contact-report-resolution-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.contact-report-resolution-summary div,
.contact-report-resolution-detail {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
}
.contact-report-resolution-summary span,
.contact-report-resolution-detail span {
  display: block;
  color: #6b7280;
  font-size: 0.78em;
  font-weight: 700;
  margin-bottom: 4px;
}
.contact-report-resolution-summary strong {
  display: block;
  color: #111827;
  line-height: 1.3;
}
.contact-report-resolution-detail p {
  margin: 0;
  color: #374151;
  line-height: 1.45;
}
.btn-xs {
  padding: 5px 9px;
  font-size: 0.82em;
}
.btn-danger-light {
  background: #fff1f2;
  border-color: #fecdd3;
  color: #b42318;
}
.refund-modal-container {
  width: min(720px, calc(100% - 24px));
}
.refund-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
}
.refund-detail-grid p {
  margin: 0;
}
@media (max-width: 640px) {
  .contact-report-resolution-summary {
    grid-template-columns: 1fr;
  }
}

/* --- Responsive Styles --- */
@media (max-width: 800px) {
  .column-right { padding-left: 15px; padding-right: 15px; padding-top: 70px; }
  .main-container { margin: 10px 0; }
  .dashboard-grid { grid-template-columns: 1fr; }
  td, th { font-size: 0.85em; padding: 6px 8px; }
  .dashboard-section h3 { font-size: 1em; }
  .badge { font-size: 0.75em; padding: 3px 8px; }
  .section-header { flex-direction: column; align-items: flex-start; gap: 5px; }
  .section-actions { width: 100%; flex-direction: column; align-items: flex-start; gap: 8px; }
  .section-pagination-controls { margin-top: 5px; }
  .inline-inputs { flex-direction: column; align-items: stretch; }
  .refund-detail-grid { grid-template-columns: 1fr; }
}
</style>
